import { Injectable, inject, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, forkJoin, from, of, throwError } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../environments/environments';
import { AuthService } from './auth.service';
import { CltValidationRulesService } from './clt-validation-rules.service';
import {
  BranchOperation,
  BranchOfficeSyncResult,
  ScheduleRuleTemplate,
  JourneyRule,
  RoleMapping,
  Employee,
  VacationRecord,
  EmployeeWeeklySchedule,
  ShiftCell,
  HourlyDemandSlot,
  HolidayEvent,
  TimecardAdherenceRecord,
  TimecardCorrectionTicket,
  AuditLogEntry,
  SystemUserAccount,
  DailyOperationHours,
  SystemNotificationItem,
  OrderHistoryDayCurve,
  OrderHistoryMonthlyStat,
  OrderHistorySlotRecord,
  ForecastScenarioMode,
  ForecastIntelligenceMetrics,
  CoverageVsDemandSlot,
  ScheduleCopilotSummary,
  EligibleSwapCandidate,
  ShiftBadgeType,
  RegisteredContactOption,
} from '../models/workforce.model';
import { SearchableSelectOption } from '../../shared/ui/searchable-select/searchable-select.component';
import {
  ApiAuditLog,
  ApiBatchDirectorUpdate,
  ApiBatchRegionalManagerUpdate,
  ApiBranch,
  ApiEmployee,
  ApiEmployeeAbsence,
  ApiEmployeeBatchUpdate,
  ApiErrorResponse,
  ApiHolidayEvent,
  ApiJobRole,
  ApiJobRoleSyncResult,
  ApiPagedResult,
  ApiScaleRule,
  ApiScheduleShift,
  ApiTimecardPunch,
  ApiTimecardTicket,
  ApiTimecardTicketMessage,
  ApiUser,
  ApiUserWrite,
  ApiWeeklySchedule,
  ApiWorkSchedulePattern,
} from '../models/api.models';
import {
  WeekDay,
  addDays,
  buildWeekDays,
  changedCells,
  extractDomingoFolgaMes,
  formatMinutesAsTime,
  fromTicketStatus,
  fromVacationType,
  resolveEffectiveWeeklyHours,
  resolveScaleDefaults,
  sundayOf,
  toAuditLogEntry,
  toBranchOperation,
  toBranchPayload,
  toEmployee,
  toEmployeePayload,
  toHoliday,
  toJobRoleWrite,
  toJourneyRule,
  toRoleMapping,
  toScheduleRule,
  toShiftUpdate,
  toSystemUser,
  toTicket,
  toTicketMessage,
  toTimecardRecord,
  toUserPayload,
  toVacationRecord,
  toWeekRows,
  toWorkSchedulePatternWrite,
} from '../mappers/workforce-api.mapper';
import {
  SectorTagValidation,
  buildSectorTagCatalog,
  normalizeSectorTag,
  validateNewSectorTag,
} from '../utils/sector-tags';
import {
  FIREBASE_DAY_CURVES,
  FIREBASE_HOURLY_DEMAND,
  FIREBASE_MONTHLY_STATS,
  FIREBASE_NOTIFICATIONS,
  FIREBASE_RECENT_ORDER_SLOTS,
  FIREBASE_TOTAL_OPERATED_DAYS,
  FIREBASE_TOTAL_ORDERS_RECORDS,
  FIREBASE_TOTAL_ORDERS_VOLUME,
} from '../data/firebase-seed.data';

const BRANCH_STORAGE_KEY = 'wfm.selectedBranchId';

/** State of the punches of the selected branch (timecard screen and unified report). */
export type TimecardLoadState = 'IDLE' | 'LOADING' | 'LOADED' | 'FORBIDDEN' | 'ERROR';

/** Fields of the batch update; only the filled ones are sent to the API. */
export interface EmployeeBatchChanges {
  /** Id of the work schedule pattern (work-schedule-patterns). */
  regraJornadaId?: number;
  horasContratuaisSemanais?: number;
  jornadaDiariaMinutos?: number;
  genero?: 'M' | 'F';
  setorTags?: string[];
}

export interface EmployeeContractChanges {
  regraJornadaId?: number;
  horasContratuaisSemanais: number | null;
  jornadaDiariaMinutos: number | null;
  genero: 'M' | 'F';
  setorTags: string[];
}

@Injectable({
  providedIn: 'root',
})
export class WorkforceStoreService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cltRules = inject(CltValidationRulesService);
  private readonly api = environment.WORKFORCE_API;

  readonly totalHistoricalOrderRecords = FIREBASE_TOTAL_ORDERS_RECORDS;
  readonly totalHistoricalOrderVolume = FIREBASE_TOTAL_ORDERS_VOLUME;
  readonly totalHistoricalOperatedDays = FIREBASE_TOTAL_OPERATED_DAYS;

  private buildDefaultHours(abertura = '11:30', fechamento = '23:00'): DailyOperationHours[] {
    const days: DailyOperationHours['diaSemana'][] = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];
    return days.map((diaSemana) => ({
      diaSemana,
      abertura,
      fechamento,
      fechado: false,
    }));
  }

  // ── 1. REGRAS DE ESCALA (API) ──
  private readonly _scheduleRules = signal<ScheduleRuleTemplate[]>([]);
  readonly scheduleRules = this._scheduleRules.asReadonly();

  // ── 2. FILIAIS (API, já filtradas pelo vínculo do usuário) ──
  private readonly _branches = signal<BranchOperation[]>([]);
  readonly branches = this._branches.asReadonly();

  // ── 3. JORNADAS (work-schedule-patterns) & FUNÇÕES DO RM (job-roles), ambas da API ──
  private readonly _schedulePatterns = signal<ApiWorkSchedulePattern[]>([]);
  readonly journeyRules = computed<JourneyRule[]>(() => this._schedulePatterns().map(toJourneyRule));
  private readonly patternById = computed(
    () => new Map(this._schedulePatterns().map((p) => [p.id, p] as const))
  );

  private readonly _jobRoles = signal<ApiJobRole[]>([]);
  readonly roleMappings = computed<RoleMapping[]>(() => this._jobRoles().map(toRoleMapping));
  private readonly jobRoleById = computed(() => new Map(this._jobRoles().map((r) => [r.id, r] as const)));

  // ── 4. COLABORADORES DA FILIAL SELECIONADA (API) ──
  private readonly _employees = signal<Employee[]>([]);
  readonly employees = this._employees.asReadonly();

  // ── 5. FÉRIAS E AFASTAMENTOS DA FILIAL SELECIONADA (API) ──
  private readonly _vacations = signal<VacationRecord[]>([]);
  readonly vacations = this._vacations.asReadonly();

  // ── 6. ESCALA DA SEMANA SELECIONADA (API) ──
  private readonly _weeklySchedules = signal<EmployeeWeeklySchedule[]>([]);
  readonly weeklySchedules = this._weeklySchedules.asReadonly();

  // ── 7. DEMANDA HORA A HORA REAL DE H001 (9.943 registros / 65.608 pedidos) ──
  private readonly _hourlyDemand = signal<HourlyDemandSlot[]>(FIREBASE_HOURLY_DEMAND);
  readonly hourlyDemand = this._hourlyDemand.asReadonly();

  private readonly _dayCurves = signal<OrderHistoryDayCurve[]>(FIREBASE_DAY_CURVES);
  readonly dayCurves = this._dayCurves.asReadonly();

  private readonly _monthlyStats = signal<OrderHistoryMonthlyStat[]>(FIREBASE_MONTHLY_STATS);
  readonly monthlyStats = this._monthlyStats.asReadonly();

  private readonly _recentOrderSlots = signal<OrderHistorySlotRecord[]>(FIREBASE_RECENT_ORDER_SLOTS);
  readonly recentOrderSlots = this._recentOrderSlots.asReadonly();

  // ── 8. FERIADOS & EVENTOS COM FATOR DE IMPACTO (Independência do Brasil 2026-09-07 1.2x) ──
  private readonly _holidays = signal<HolidayEvent[]>([]);
  readonly holidays = this._holidays.asReadonly();

  // ── 9. NOTIFICAÇÕES DO SISTEMA (3 Notificações Reais do Firebase) ──
  private readonly _systemNotifications = signal<SystemNotificationItem[]>(FIREBASE_NOTIFICATIONS);
  readonly systemNotifications = this._systemNotifications.asReadonly();

  // ── 10. ADERÊNCIA ESCALA x PONTO (batidas importadas da filial: 4 previstas x 4 realizadas) ──
  private readonly _timecardRecords = signal<TimecardAdherenceRecord[]>([]);
  readonly timecardRecords = this._timecardRecords.asReadonly();
  private readonly _timecardLoadState = signal<TimecardLoadState>('IDLE');
  readonly timecardLoadState = this._timecardLoadState.asReadonly();

  // ── Tags de setor: padrões + usadas pelos colaboradores + criadas pelo usuário ──
  private readonly _customSectorTags = signal<string[]>([]);
  readonly sectorTagCatalog = computed<string[]>(() =>
    buildSectorTagCatalog(
      [
        ...this._employees().map((e) => (e.setorTags?.length ? e.setorTags : e.setoresTags)),
        ...this._weeklySchedules().map((r) => (r.setorTags?.length ? r.setorTags : r.setoresTags)),
      ],
      this._customSectorTags()
    )
  );

  private readonly _correctionTickets = signal<TimecardCorrectionTicket[]>([]);
  readonly correctionTickets = this._correctionTickets.asReadonly();

  // ── 11. USUÁRIOS E AUDITORIA (API, só para Admin) ──
  private readonly _systemUsers = signal<SystemUserAccount[]>([]);
  readonly systemUsers = this._systemUsers.asReadonly();

  private readonly _auditLogs = signal<AuditLogEntry[]>([]);
  readonly auditLogs = this._auditLogs.asReadonly();

  // ── COMPUTED: KPIs Globais ──
  readonly activeBranchesCount = computed(() =>
    this._branches().filter((b) => b.status === 'ATIVA').length
  );

  readonly activeEmployeesCount = computed(() =>
    this._employees().filter((e) => e.situacaoReal === 'ATIVO').length
  );

  readonly publishedSchedulesPct = computed(() => {
    const list = this._branches();
    if (list.length === 0) return 0;
    const pub = list.filter((b) => b.escalaPublicada).length;
    return Math.round((pub / list.length) * 100);
  });

  readonly averageEfficiencyScore = computed(() => {
    const list = this._branches();
    if (list.length === 0) return 0;
    const sum = list.reduce((acc, b) => acc + b.eficienciaMedia, 0);
    return Number((sum / list.length).toFixed(1));
  });

  // ── MÉTODOS DE NEGÓCIO & MUTAÇÕES COM AUDITORIA ──

  // ── Filial e semana selecionadas ──
  private readonly _selectedBranchId = signal<number | null>(null);
  readonly selectedBranchId = this._selectedBranchId.asReadonly();
  readonly selectedBranch = computed(
    () => this._branches().find((b) => b.id === String(this._selectedBranchId())) ?? null
  );

  // ── Opções pesquisáveis de Filial (Código + Nome), Colaboradores e Contatos Cadastrados ──
  readonly branchSelectOptions = computed<SearchableSelectOption<number>[]>(() =>
    this._branches().map((b) => ({
      value: Number(b.id),
      badge: b.codFilial,
      label: b.nomeFilialBi,
      subtitle: [b.codProtheus, b.cidade && b.uf ? `${b.cidade}/${b.uf}` : b.cidade, b.marca || b.segmento]
        .filter(Boolean)
        .join(' · '),
      searchKeywords: `${b.codFilial} ${b.codProtheus} ${b.nomeFilialBi} ${b.nomeFilialProtheus} ${b.cidade} ${b.uf} ${b.marca} ${b.segmento} ${b.codDiretor} ${b.nomeDiretor} ${b.codGoOp} ${b.nomeGoOp}`,
      icon: 'storefront',
    }))
  );

  readonly employeeSelectOptions = computed<SearchableSelectOption<string>[]>(() =>
    this._employees().map((e) => ({
      value: e.chapa,
      badge: `Chapa ${e.chapa}`,
      label: e.nome,
      subtitle: `${e.funcao} · ${e.setor} (${e.cargoNivel === 'LIDERANCA' ? 'Liderança' : 'Base'})`,
      searchKeywords: `${e.chapa} ${e.nome} ${e.funcao} ${e.setor} ${e.codFilial}`,
      icon: 'person',
    }))
  );

  private readonly _customContacts = signal<RegisteredContactOption[]>([
    { code: 'DR.01', name: 'DIRETOR 01 - OPERAÇÕES SUL', role: 'DIRETOR', email: 'diretoria.sul@grupomadero.com.br' },
    { code: 'DR.02', name: 'DIRETOR 02 - OPERAÇÕES SP CAPITAL', role: 'DIRETOR', email: 'diretoria.sp@grupomadero.com.br' },
    { code: 'DR.03', name: 'DIRETOR 03 - OPERAÇÕES SP INTERIOR', role: 'DIRETOR', email: 'diretoria.spi@grupomadero.com.br' },
    { code: 'DR.04', name: 'DIRETOR 04 - OPERAÇÕES RJ / ES', role: 'DIRETOR', email: 'diretoria.rj@grupomadero.com.br' },
    { code: 'DR.05', name: 'DIRETOR 05 - OPERAÇÕES MG / CENTRO-OESTE', role: 'DIRETOR', email: 'diretoria.mg@grupomadero.com.br' },
    { code: 'DR.06', name: 'DIRETOR 06 - OPERAÇÕES NORDESTE', role: 'DIRETOR', email: 'diretoria.ne@grupomadero.com.br' },
    { code: 'RG.010', name: 'GERENTE REGIONAL 10 - CURITIBA / PR', role: 'GERENTE_REGIONAL', email: 'regional10@grupomadero.com.br' },
    { code: 'RG.011', name: 'GERENTE REGIONAL 11 - SÃO PAULO SUL', role: 'GERENTE_REGIONAL', email: 'regional11@grupomadero.com.br' },
    { code: 'RG.012', name: 'GERENTE REGIONAL 12 - CAMPINAS / INTERIOR', role: 'GERENTE_REGIONAL', email: 'regional12@grupomadero.com.br' },
    { code: 'RG.013', name: 'GERENTE REGIONAL 13 - RIO DE JANEIRO', role: 'GERENTE_REGIONAL', email: 'regional13@grupomadero.com.br' },
    { code: 'RG.014', name: 'GERENTE REGIONAL 14 - MINAS GERAIS', role: 'GERENTE_REGIONAL', email: 'regional14@grupomadero.com.br' },
    { code: 'RG.015', name: 'GERENTE REGIONAL 15 - BRASÍLIA / GO', role: 'GERENTE_REGIONAL', email: 'regional15@grupomadero.com.br' },
  ]);

  readonly registeredDirectors = computed<RegisteredContactOption[]>(() => {
    const mapByCode = new Map<string, RegisteredContactOption>();
    for (const c of this._customContacts().filter((x) => x.role === 'DIRETOR')) {
      mapByCode.set(c.code.toUpperCase(), { ...c, branchesCount: 0 });
    }
    for (const u of this._systemUsers().filter((x) => x.perfil === 'Diretor')) {
      const code = (u.cargo && /^DR\.\d+/i.test(u.cargo) ? u.cargo : `DR.${u.id}`).toUpperCase();
      mapByCode.set(code, {
        code,
        name: u.nome,
        role: 'DIRETOR',
        email: u.email,
        branchesCount: 0,
      });
    }
    for (const b of this._branches()) {
      const code = (b.codDiretor || '').trim().toUpperCase();
      if (!code) continue;
      const existing = mapByCode.get(code);
      mapByCode.set(code, {
        code,
        name: b.nomeDiretor || existing?.name || code,
        role: 'DIRETOR',
        email: existing?.email,
        branchesCount: (existing?.branchesCount ?? 0) + 1,
      });
    }
    return [...mapByCode.values()].sort((a, b) => a.code.localeCompare(b.code));
  });

  readonly registeredRegionalManagers = computed<RegisteredContactOption[]>(() => {
    const mapByCode = new Map<string, RegisteredContactOption>();
    for (const c of this._customContacts().filter((x) => x.role === 'GERENTE_REGIONAL')) {
      mapByCode.set(c.code.toUpperCase(), { ...c, branchesCount: 0 });
    }
    for (const u of this._systemUsers().filter((x) => x.perfil === 'Gerente Regional')) {
      const code = (u.cargo && /^RG\.\d+/i.test(u.cargo) ? u.cargo : `RG.${u.id}`).toUpperCase();
      mapByCode.set(code, {
        code,
        name: u.nome,
        role: 'GERENTE_REGIONAL',
        email: u.email,
        branchesCount: 0,
      });
    }
    for (const b of this._branches()) {
      const code = (b.codGoOp || '').trim().toUpperCase();
      if (!code) continue;
      const existing = mapByCode.get(code);
      mapByCode.set(code, {
        code,
        name: b.nomeGoOp || existing?.name || code,
        role: 'GERENTE_REGIONAL',
        email: existing?.email,
        branchesCount: (existing?.branchesCount ?? 0) + 1,
      });
    }
    return [...mapByCode.values()].sort((a, b) => a.code.localeCompare(b.code));
  });

  readonly registeredStoreManagers = computed<RegisteredContactOption[]>(() => {
    const list: RegisteredContactOption[] = [];
    const seen = new Set<string>();
    for (const u of this._systemUsers().filter((x) => x.perfil === 'Gestor' || x.perfil === 'Admin')) {
      const key = u.username.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      list.push({
        code: u.username,
        name: u.nome,
        role: 'GESTOR_LOJA',
        email: u.email,
        subtitle: `${u.perfil} · ${u.email || u.username}`,
      });
    }
    for (const e of this._employees().filter((x) => x.cargoNivel === 'LIDERANCA')) {
      const key = `chapa-${e.chapa}`;
      if (seen.has(key)) continue;
      seen.add(key);
      list.push({
        code: e.chapa,
        name: e.nome,
        role: 'GESTOR_LOJA',
        email: `${e.codFilial.toLowerCase()}@grupomadero.com.br`,
        subtitle: `${e.funcao} · Filial ${e.codFilial}`,
      });
    }
    return list;
  });

  addCustomContact(contact: RegisteredContactOption): void {
    this._customContacts.update((list) => [
      ...list.filter((c) => !(c.role === contact.role && c.code.toUpperCase() === contact.code.toUpperCase())),
      { ...contact, code: contact.code.trim().toUpperCase(), name: contact.name.trim() },
    ]);
  }

  private readonly _weekStart = signal<string>(sundayOf(new Date()));
  /** Sunday of the selected week (yyyy-MM-dd). */
  readonly weekStart = this._weekStart.asReadonly();
  readonly weekDays = computed<WeekDay[]>(() =>
    buildWeekDays(this._weekStart(), this._holidays())
  );

  private readonly _currentSchedule = signal<{
    id: number;
    status: string;
    publishedAt?: string | null;
    efficiencyScore?: number | null;
    pearsonCorrelation?: number | null;
    demandBalanceScore?: number | null;
    idealHeadcount?: number | null;
  } | null>(null);
  /** Schedule of the selected branch and week, or null when it was never generated. */
  readonly currentSchedule = this._currentSchedule.asReadonly();
  readonly isSchedulePublished = computed(() => this._currentSchedule()?.status === 'PUBLISHED');

  get currentApiScheduleId(): number | null {
    return this._currentSchedule()?.id ?? null;
  }

  private readonly _scheduleLoading = signal(false);
  readonly scheduleLoading = this._scheduleLoading.asReadonly();
  private readonly _scheduleSaving = signal(false);
  readonly scheduleSaving = this._scheduleSaving.asReadonly();

  private readonly _auditTotal = signal(0);
  readonly auditTotal = this._auditTotal.asReadonly();
  private readonly _auditQuery = signal({ page: 1, pageSize: 50, search: '', module: '' });
  readonly auditQuery = this._auditQuery.asReadonly();

  /** Raw API records, kept so an update sends back the fields the screens do not edit. */
  private apiBranches = new Map<number, ApiBranch>();
  private apiEmployees = new Map<number, ApiEmployee>();
  private apiUsers = new Map<number, ApiUser>();
  /** Latest concurrency token of each shift, so an undo or a second edit never sends a stale one. */
  private readonly shiftVersions = new Map<number, string | undefined>();
  /** Responses for a branch or week the user already left are ignored. */
  private branchLoadSeq = 0;
  private weekLoadSeq = 0;
  private timecardLoadSeq = 0;

  constructor() {
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      if (user && this.authService.isAuthenticated()) {
        this.loadInitialDataFromApi();
      }
    });
  }

  /** Reference data for every screen, then the data of the selected branch. */
  loadInitialDataFromApi(): void {
    if (!this.authService.isAuthenticated()) return;

    forkJoin({
      rules: this.http.get<ApiScaleRule[]>(`${this.api}scale-rules`).pipe(catchError(() => of([] as ApiScaleRule[]))),
      branches: this.http.get<ApiBranch[]>(`${this.api}branches`),
      jobRoles: this.http.get<ApiJobRole[]>(`${this.api}job-roles`).pipe(catchError(() => of([] as ApiJobRole[]))),
      holidays: this.http.get<ApiHolidayEvent[]>(`${this.api}holiday-events`).pipe(catchError(() => of([] as ApiHolidayEvent[]))),
    }).subscribe({
      next: ({ rules, branches, jobRoles, holidays }) => {
        const mappedRules = rules.map(toScheduleRule);
        this._scheduleRules.set(mappedRules);
        this.apiBranches = new Map(branches.map((b) => [b.id, b]));
        this._branches.set(branches.map((b) => toBranchOperation(b, mappedRules)));
        this._jobRoles.set(jobRoles);
        this._holidays.set(holidays.map(toHoliday));

        const preferred = this._selectedBranchId() ?? this.readRememberedBranch();
        const initial =
          branches.find((b) => b.id === preferred) ?? branches.find((b) => b.isActive && b.active) ?? branches[0];
        if (initial) {
          this.selectBranch(initial.id);
        } else {
          this.clearBranchData();
        }
      },
      error: (err) => this.notifyError(err, 'Não foi possível carregar os dados iniciais.'),
    });

    this.loadSchedulePatterns();
    this.loadNotifications();

    if (this.authService.isAdminOrRoot()) {
      this.loadUsers();
      this.loadAuditLogs();
    }
  }

  loadNotifications(): void {
    this.http.get<Record<string, unknown>[]>(`${this.api}Dashboard/notifications`).subscribe({
      next: (items) => {
        if (Array.isArray(items) && items.length > 0) {
          this._systemNotifications.set(
            items.map((n, idx) => ({
              id: String(n['id'] ?? `notif-${idx}`),
              timestamp: String(n['timestamp'] ?? n['createdAt'] ?? ''),
              type: String(n['type'] ?? 'INFO'),
              title: String(n['title'] ?? ''),
              message: String(n['message'] ?? ''),
              targetEmployeeId: String(n['targetEmployeeId'] ?? ''),
              targetDate: String(n['targetDate'] ?? ''),
              ticketId: String(n['ticketId'] ?? ''),
              readBy: Array.isArray(n['readBy']) ? (n['readBy'] as string[]) : [],
            }))
          );
        }
      },
      error: () => {
        // Mantém notificações padrão se o endpoint não retornar dados
      },
    });
  }

  loadBranchDemandAndForecast(branchId: number): void {
    this.http
      .get<Record<string, unknown>[] | Record<string, unknown>>(
        `${this.api}weekly-schedules/branch/${branchId}/forecast`
      )
      .subscribe({
        next: (forecast) => {
          if (Array.isArray(forecast)) {
            if (forecast.length === 0) return;
            const daySiglas: OrderHistoryDayCurve['sigla'][] = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
            const dayNames = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
            const byDay = new Map<number, Map<number, number>>();
            for (const item of forecast) {
              const dIdx = Number(item['dayOfWeekIndex'] ?? 0);
              const hSlot = Number(item['hourSlot'] ?? 0);
              const orders = Number(
                item['projectedOrders'] ?? item['ewmaOrders'] ?? item['simpleMeanOrders'] ?? 0
              );
              const hourMap = byDay.get(dIdx) ?? new Map<number, number>();
              hourMap.set(hSlot, (hourMap.get(hSlot) ?? 0) + orders);
              byDay.set(dIdx, hourMap);
            }
            const nextCurves: OrderHistoryDayCurve[] = daySiglas.map((sigla, idx) => {
              const hourMap = byDay.get(idx);
              const porHora: Record<string, number> = {};
              let mediaDiaria = 0;
              if (hourMap) {
                for (const [h, val] of hourMap.entries()) {
                  const hh = `${String(h).padStart(2, '0')}:00`;
                  const rounded = Math.round(val);
                  porHora[hh] = rounded;
                  mediaDiaria += rounded;
                }
              }
              return {
                diaSemana: dayNames[idx],
                sigla,
                diasAmostrados: 8,
                totalPedidos: mediaDiaria * 8,
                mediaDiariaPedidos: mediaDiaria,
                porHora,
              };
            });
            if (nextCurves.some((c) => c.mediaDiariaPedidos > 0)) {
              this._dayCurves.set(nextCurves);
              const refCurve = nextCurves.find((c) => c.sigla === 'SEX' && c.mediaDiariaPedidos > 0) ??
                nextCurves.find((c) => c.mediaDiariaPedidos > 0);
              if (refCurve) {
                const slots: HourlyDemandSlot[] = [];
                for (let h = 9; h <= 23; h++) {
                  const hora = `${String(h).padStart(2, '0')}:00`;
                  const pedidos = refCurve.porHora[hora] ?? 0;
                  const ideal = Math.max(4, Math.min(16, Math.ceil(pedidos / 4.2) + 3));
                  const coz = Math.max(2, Math.round(ideal * 0.46));
                  const sal = Math.max(2, ideal - coz);
                  slots.push({
                    hora,
                    pedidosPrevistos: pedidos,
                    staffCozinhaEscalado: coz,
                    staffSalaoEscalado: sal,
                    staffTotalEscalado: coz + sal,
                    staffIdeal: ideal,
                    isPico: pedidos >= 35,
                  });
                }
                this._hourlyDemand.set(slots);
              }
            }
            return;
          }
          const slots = forecast?.['hourlyDemand'] ?? forecast?.['slots'];
          if (Array.isArray(slots) && slots.length > 0) {
            this._hourlyDemand.set(slots as HourlyDemandSlot[]);
          }
          const curves = forecast?.['dayCurves'];
          if (Array.isArray(curves) && curves.length > 0) {
            this._dayCurves.set(curves as OrderHistoryDayCurve[]);
          }
        },
        error: () => {
          // Mantém curvas locais quando o forecast remoto não está disponível
        },
      });

    this.http.get<Record<string, unknown>[]>(`${this.api}order-demand-histories/branch/${branchId}`).subscribe({
      next: (history) => {
        if (Array.isArray(history) && history.length > 0) {
          const dayLabels = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];
          this._recentOrderSlots.set(
            history.slice(0, 100).map((h, idx) => {
              const refDate = String(h['referenceDate'] ?? '').slice(0, 10);
              const [refY, refM, refD] = /^\d{4}-\d{2}-\d{2}$/.test(refDate)
                ? refDate.split('-')
                : ['', '', ''];
              const parsedDow =
                refY && refM && refD
                  ? dayLabels[new Date(Number(refY), Number(refM) - 1, Number(refD)).getDay()] ?? ''
                  : '';
              const hasHourSlot = h['hourSlot'] !== undefined && h['hourSlot'] !== null;
              const isHalfHour = Boolean(h['halfHourSlot']) && Number(h['halfHourSlot']) > 0;
              const formattedHour = hasHourSlot
                ? `${String(Number(h['hourSlot'])).padStart(2, '0')}:${isHalfHour ? '30' : '00'}`
                : String(h['saleHour'] ?? h['horaVenda'] ?? '');
              return {
                id: String(h['id'] ?? `slot-${idx}`),
                codFilial: String(h['branchCode'] ?? h['codFilial'] ?? this.selectedBranch()?.codFilial ?? ''),
                nomeFilial: String(h['branchName'] ?? h['nomeFilial'] ?? this.selectedBranch()?.nomeFilialBi ?? ''),
                ano: String(refY || h['year'] || h['ano'] || ''),
                mes: String(refM || h['month'] || h['mes'] || ''),
                dia: String(refD || h['day'] || h['dia'] || ''),
                diaSemana: String(parsedDow || h['dayOfWeek'] || h['diaSemana'] || ''),
                horaVenda: formattedHour,
                qtdPedidos: Number(h['totalOrders'] ?? h['orderCount'] ?? h['qtdPedidos'] ?? 0),
              };
            })
          );
        }
      },
      error: () => {
        // Mantém histórico local quando a API não retorna registros
      },
    });
  }

  /** Work schedule patterns (6X1, 5X2...). Loaded apart so a failure here does not block the other screens. */
  loadSchedulePatterns(): void {
    this.http.get<ApiWorkSchedulePattern[]>(`${this.api}work-schedule-patterns`).subscribe({
      next: (patterns) => {
        this._schedulePatterns.set(patterns ?? []);
        this.refreshEmployeesFromApi();
      },
      error: (err) => this.notifyError(err, 'Não foi possível carregar as jornadas.'),
    });
  }

  selectBranch(branchId: number): void {
    this._selectedBranchId.set(branchId);
    try {
      localStorage.setItem(BRANCH_STORAGE_KEY, String(branchId));
    } catch {
      // Storage blocked: the choice just is not remembered.
    }
    this.loadBranchData();
  }

  private readRememberedBranch(): number | null {
    try {
      const value = Number(localStorage.getItem(BRANCH_STORAGE_KEY));
      return Number.isInteger(value) && value > 0 ? value : null;
    } catch {
      return null;
    }
  }

  private clearBranchData(): void {
    this._employees.set([]);
    this._vacations.set([]);
    this._correctionTickets.set([]);
    this.clearTimecard();
    this.clearSchedule();
  }

  private mapEmployee(e: ApiEmployee): Employee {
    return toEmployee(e, this.jobRoleById(), this.selectedBranch() ?? undefined, this.patternById());
  }

  /** Re-maps the loaded employees after job roles or patterns change, keeping the week grid in sync. */
  private refreshEmployeesFromApi(): void {
    if (this.apiEmployees.size === 0) return;
    const mapped = this._employees().map((emp) => {
      const raw = this.apiEmployees.get(Number(emp.id));
      return raw ? this.mapEmployee(raw) : emp;
    });
    this._employees.set(mapped);
    const byId = new Map(mapped.map((e) => [e.id, e]));
    this._weeklySchedules.update((rows) =>
      rows.map((row) => {
        const emp = byId.get(row.employeeId);
        if (!emp) return row;
        const patternId = Number(emp.regraJornadaId);
        const meta = this.resolveScaleModelMeta(
          emp.regraJornadaNome,
          emp.horasContratuaisSemanais,
          Number.isInteger(patternId) && patternId > 0 ? patternId : undefined,
          emp.jornadaDiariaMinutos
        );
        const tags = emp.setorTags?.length ? emp.setorTags : [emp.setor];
        return {
          ...row,
          funcao: emp.funcao,
          cargoNivel: emp.cargoNivel,
          setor: emp.setor,
          setorTags: tags,
          setoresTags: tags,
          categoriaFuncao: emp.categoriaFuncao,
          contabilizaProducaoPedidos: emp.contabilizaProducaoPedidos,
          regraJornadaNome: emp.regraJornadaNome,
          modeloEscala: meta.modeloEscala,
          folgasSemanaisPadrao: meta.folgasSemanaisPadrao,
          folgasSemanaisObrigatorias: meta.folgasSemanaisObrigatorias,
          jornadaDiariaMinutos: emp.jornadaDiariaMinutos,
          horasContratuais: meta.horasContratuais,
        };
      })
    );
  }

  /** Employees, absences and tickets of the selected branch only (300 stores never load at once). */
  private loadBranchData(): void {
    const branchId = this._selectedBranchId();
    if (branchId == null) return;
    const seq = ++this.branchLoadSeq;
    const branch = this.selectedBranch() ?? undefined;
    const params = { branchId };
    this.clearTimecard();
    this.loadBranchDemandAndForecast(branchId);

    forkJoin({
      employees: this.http.get<ApiEmployee[]>(`${this.api}employees`, { params }),
      absences: this.http.get<ApiEmployeeAbsence[]>(`${this.api}employee-absences`, { params }),
      tickets: this.http.get<ApiTimecardTicket[]>(`${this.api}timecard-tickets`, { params }),
    }).subscribe({
      next: ({ employees, absences, tickets }) => {
        if (seq !== this.branchLoadSeq) return;
        this.apiEmployees = new Map(employees.map((e) => [e.id, e]));
        const mapped = employees.map((e) => this.mapEmployee(e));
        this._employees.set(mapped);
        const byId = new Map(mapped.map((e) => [Number(e.id), e]));
        this._vacations.set(absences.map((a) => toVacationRecord(a, byId.get(a.employeeId))));
        this._correctionTickets.set(tickets.map((t) => toTicket(t, branch)));
        this.loadWeekSchedule();
        this.loadTimecardPunches(branchId);
      },
      error: (err) => {
        if (seq !== this.branchLoadSeq) return;
        this.clearBranchData();
        this.notifyError(err, 'Não foi possível carregar os dados da filial.');
      },
    });
  }

  // ── Batidas de ponto importadas (só Admin e Equipe de Ponto) ──

  private clearTimecard(): void {
    this.timecardLoadSeq++;
    this._timecardRecords.set([]);
    this._timecardLoadState.set('IDLE');
  }

  /**
   * Punches of the branch. The endpoint is restricted to Admin and Timecard team: other profiles
   * (and a 403 from the API) just see the empty state, without an error message.
   */
  loadTimecardPunches(branchId: number | null = this._selectedBranchId()): void {
    if (branchId == null) return;
    const seq = ++this.timecardLoadSeq;
    this._timecardLoadState.set('LOADING');
    this.http.get<ApiTimecardPunch[]>(`${this.api}timecard-tickets/punches/branch/${branchId}`).subscribe({
      next: (punches) => {
        if (seq !== this.timecardLoadSeq) return;
        const branch = this.selectedBranch() ?? undefined;
        const byId = new Map(this._employees().map((e) => [Number(e.id), e]));
        const byCode = new Map(this._employees().map((e) => [e.chapa, e]));
        this._timecardRecords.set(
          (punches ?? []).map((p) =>
            toTimecardRecord(
              p,
              (p.employeeId != null ? byId.get(p.employeeId) : undefined) ?? byCode.get(p.employeeCode ?? ''),
              branch
            )
          )
        );
        this._timecardLoadState.set('LOADED');
      },
      error: (err: HttpErrorResponse) => {
        if (seq !== this.timecardLoadSeq) return;
        this._timecardRecords.set([]);
        if (err?.status === 403) {
          this._timecardLoadState.set('FORBIDDEN');
          return;
        }
        this._timecardLoadState.set('ERROR');
        this.notifyError(err, 'Não foi possível carregar as batidas de ponto da filial.');
      },
    });
  }

  // ── Escala da semana (persistida na API) ──

  goToWeek(offsetWeeks: number): void {
    this._weekStart.update((week) => addDays(week, offsetWeeks * 7));
    this.loadWeekSchedule();
  }

  goToCurrentWeek(): void {
    this._weekStart.set(sundayOf(new Date()));
    this.loadWeekSchedule();
  }

  setWeekStart(weekStartIso: string): void {
    if (!weekStartIso) return;
    this._weekStart.set(weekStartIso);
    this.loadWeekSchedule();
  }

  loadWeekSchedule(): void {
    const branchId = this._selectedBranchId();
    if (branchId == null) return;
    const seq = ++this.weekLoadSeq;
    this._scheduleLoading.set(true);

    this.http
      .get<ApiWeeklySchedule | null>(`${this.api}weekly-schedules/branch/${branchId}/weeks/${this._weekStart()}`)
      .subscribe({
        next: (schedule) => {
          if (seq !== this.weekLoadSeq) return;
          this._scheduleLoading.set(false);
          // 204 (no body) means the week was never generated: the screen shows the empty state.
          if (schedule) {
            this.applySchedule(schedule);
          } else {
            this.clearSchedule();
          }
        },
        error: (err) => {
          if (seq !== this.weekLoadSeq) return;
          this._scheduleLoading.set(false);
          this.clearSchedule();
          this.notifyError(err, 'Não foi possível carregar a escala da semana.');
        },
      });
  }

  private applySchedule(schedule: ApiWeeklySchedule): void {
    this._currentSchedule.set({
      id: schedule.id,
      status: schedule.status,
      publishedAt: schedule.publishedAt,
      efficiencyScore: schedule.efficiencyScore,
      pearsonCorrelation: schedule.pearsonCorrelation,
      demandBalanceScore: schedule.demandBalanceScore,
      idealHeadcount: schedule.idealHeadcount,
    });
    this.shiftVersions.clear();
    for (const shift of schedule.shifts) {
      this.shiftVersions.set(shift.id, shift.updatedAt);
    }
    this._weeklySchedules.set(toWeekRows(schedule, this._employees(), this.weekDays(), this.patternById()));
    this._undoHistory.set(null);
    this._breaksOptimized.set(false);
    this._branches.update((list) =>
      list.map((b) =>
        b.id === String(schedule.branchId)
          ? {
              ...b,
              escalaPublicada: schedule.status === 'PUBLISHED',
              eficienciaMedia: schedule.efficiencyScore ?? b.eficienciaMedia,
              quadroIdeal: schedule.idealHeadcount ?? b.quadroIdeal,
            }
          : b
      )
    );
  }

  private clearSchedule(): void {
    this._currentSchedule.set(null);
    this.shiftVersions.clear();
    this._weeklySchedules.set([]);
    this._undoHistory.set(null);
    this._breaksOptimized.set(false);
  }

  /** Generates (or regenerates, while still a draft) the selected week. */
  generateWeekSchedule(onSuccess?: () => void): void {
    const branchId = this._selectedBranchId();
    if (branchId == null) return;
    this._scheduleLoading.set(true);
    this.http
      .post<ApiWeeklySchedule>(`${this.api}weekly-schedules/auto-generate`, {
        branchId,
        weekStartDate: this._weekStart(),
      })
      .subscribe({
        next: (schedule) => {
          this._scheduleLoading.set(false);
          this.applySchedule(schedule);
          onSuccess?.();
        },
        error: (err) => {
          this._scheduleLoading.set(false);
          this.notifyError(err, 'Não foi possível gerar a escala.');
        },
      });
  }

  generateSmartSchedule(onSuccess?: () => void): void {
    this.generateWeekSchedule(onSuccess);
  }

  publishCurrentSchedule(onSuccess?: () => void): boolean {
    const current = this._currentSchedule();
    if (!current) {
      this.notify('Gere a escala da semana antes de publicar.');
      return false;
    }
    const violations = this.getScheduleCltViolations();
    if (violations.length > 0) {
      this.snackBar.open(
        `Publicação bloqueada preventivamente (${violations.length} violação(ões) CLT): ${violations[0]}`,
        'Fechar',
        { duration: 8000, panelClass: 'wfm-snackbar-error' }
      );
      return false;
    }
    this.http.post(`${this.api}weekly-schedules/${current.id}/publish`, {}).subscribe({
      next: () => {
        const branchIdStr = String(this._selectedBranchId());
        this._branches.update((list) =>
          list.map((b) => (b.id === branchIdStr ? { ...b, escalaPublicada: true } : b))
        );
        this._currentSchedule.update((s) => (s ? { ...s, status: 'PUBLISHED' } : s));
        this.notify('Escala publicada.');
        onSuccess?.();
        this.loadWeekSchedule();
      },
      error: (err) => {
        const branchIdStr = String(this._selectedBranchId());
        this._branches.update((list) =>
          list.map((b) => (b.id === branchIdStr ? { ...b, escalaPublicada: false } : b))
        );
        this._currentSchedule.update((s) => (s ? { ...s, status: current.status } : s));
        this.notifyError(err, 'Não foi possível publicar a escala.');
      },
    });
    return true;
  }

  getScheduleCltViolations(): string[] {
    const violations: string[] = [];
    for (const row of this._weeklySchedules()) {
      for (const cell of row.shifts) {
        if (cell.cltAlertas && cell.cltAlertas.length > 0) {
          violations.push(`${row.nome} (${cell.dayLabel}): ${cell.cltAlertas[0]}`);
        }
      }
      if (row.horasContratuais > 0 && row.horasEscaladas > row.horasContratuais + 0.1) {
        violations.push(
          `${row.nome}: carga semanal escalada (${row.horasEscaladas}h) ultrapassa a carga contratual (${row.horasContratuais}h).`
        );
      }
    }
    return violations;
  }

  /**
   * Saves, one request at a time, every cell that differs from `before`. If the API refuses one
   * (CLT rule, someone else edited it, published week), the week is reloaded with what is in the database.
   */
  private persistScheduleChanges(before: EmployeeWeeklySchedule[], justification: string): void {
    const current = this._currentSchedule();
    const changes = changedCells(before, this._weeklySchedules());
    if (!current || changes.length === 0) return;

    const hasGuidedDayOffSwap = changes.some(
      (c) => c.cell.permutarFolgaDiaIndex !== undefined && c.cell.permutarFolgaDiaIndex !== null
    );

    this._scheduleSaving.set(true);
    from(changes)
      .pipe(
        concatMap(({ employeeId, cell }) => {
          const body = toShiftUpdate(cell, current.id, Number(employeeId), justification);
          body.updatedAt = this.shiftVersions.get(cell.shiftId!) ?? body.updatedAt;
          return this.http.put<ApiScheduleShift>(`${this.api}weekly-schedules/shift/${cell.shiftId}`, body);
        })
      )
      .subscribe({
        next: (saved) => this.applySavedShift(saved),
        error: (err) => {
          this._scheduleSaving.set(false);
          this.notifyError(err, 'Não foi possível salvar a alteração da escala.');
          this.loadWeekSchedule();
        },
        complete: () => {
          this._scheduleSaving.set(false);
          if (hasGuidedDayOffSwap) {
            this.loadWeekSchedule();
          }
        },
      });
  }

  private applySavedShift(saved: ApiScheduleShift): void {
    this.shiftVersions.set(saved.id, saved.updatedAt);
    const savedDom = extractDomingoFolgaMes(saved.shiftTag);
    const rawWarn = saved.cltWarningDetails || saved.cltWarningMessage;
    const cltAlertas =
      saved.hasCltWarning && rawWarn ? rawWarn.split(/\s*\|\s*/).filter(Boolean) : [];
    this._weeklySchedules.update((rows) =>
      rows.map((row) => {
        if (row.employeeId !== String(saved.employeeId)) return row;
        const nextShifts = row.shifts.map((cell) =>
          cell.shiftId !== saved.id
            ? cell
            : {
                ...cell,
                updatedAt: saved.updatedAt,
                workedMinutes: saved.workedMinutes,
                permutarFolgaDiaIndex: undefined,
                domingoFolgaMes: savedDom ?? cell.domingoFolgaMes,
                editadoPosPublicacao:
                  saved.isPostPublicationEdit !== undefined
                    ? Boolean(saved.isPostPublicationEdit)
                    : cell.editadoPosPublicacao,
                cltAlertas,
              }
        );
        const hasAnyFolgaInWeek = nextShifts.some((s) => s.tipo === 'FOLGA');
        const hasSunFolgaInWeek = nextShifts.some((s) => s.dayIndex === 0 && s.tipo === 'FOLGA');
        const nextFolgasMesAtual =
          saved.monthlyOffDaysCount != null && (saved.monthlyOffDaysCount > 0 || !hasAnyFolgaInWeek)
            ? saved.monthlyOffDaysCount
            : row.folgasMesAtual;
        const nextDomingosFolgaMesAtual =
          saved.monthlySundayOffCount != null && (saved.monthlySundayOffCount > 0 || !hasSunFolgaInWeek)
            ? saved.monthlySundayOffCount
            : row.domingosFolgaMesAtual;
        return this.revalidateEmployeeWeekAlerts({
          ...row,
          folgasMesAtual: nextFolgasMesAtual,
          folgasMesPrevistas: saved.monthlyOffDaysTarget ?? row.folgasMesPrevistas,
          domingosFolgaMesAtual: nextDomingosFolgaMesAtual,
          domingosFolgaMesPrevistos: saved.monthlySundayOffTarget ?? row.domingosFolgaMesPrevistos,
          horasContratuais: resolveEffectiveWeeklyHours(
            row.modeloEscala,
            row.horasContratuais,
            nextShifts,
            saved.effectiveWeeklyHoursLimit
          ),
          shifts: nextShifts,
        });
      })
    );
  }

  // ── Administração ──

  private loadUsers(): void {
    this.http.get<ApiUser[]>(`${this.api}users`).subscribe({
      next: (users) => {
        this.apiUsers = new Map(users.map((u) => [u.id, u]));
        this._systemUsers.set(users.map(toSystemUser));
      },
      error: (err) => this.notifyError(err, 'Não foi possível carregar os usuários.'),
    });
  }

  /** Audit is paged and filtered on the server; the table never holds more than one page. */
  loadAuditLogs(query: Partial<{ page: number; pageSize: number; search: string; module: string }> = {}): void {
    const next = { ...this._auditQuery(), ...query };
    this._auditQuery.set(next);
    const params: Record<string, string | number> = { page: next.page, pageSize: next.pageSize };
    if (next.search.trim()) params['search'] = next.search.trim();
    if (next.module) params['module'] = next.module;

    this.http.get<ApiPagedResult<ApiAuditLog>>(`${this.api}audit-logs`, { params }).subscribe({
      next: (result) => {
        this._auditLogs.set(result.items.map(toAuditLogEntry));
        this._auditTotal.set(result.totalCount);
      },
      error: (err) => this.notifyError(err, 'Não foi possível carregar a auditoria.'),
    });
  }

  fetchAllAuditLogsForExport(
    query: Partial<{ search: string; module: string }> = {}
  ): Observable<AuditLogEntry[]> {
    const params: Record<string, string | number> = { page: 1, pageSize: 1000 };
    if (query.search?.trim()) params['search'] = query.search.trim();
    if (query.module?.trim()) params['module'] = query.module.trim();

    return this.http.get<ApiPagedResult<ApiAuditLog>>(`${this.api}audit-logs`, { params }).pipe(
      map((result) => {
        const mapped = (result?.items ?? []).map(toAuditLogEntry);
        if (mapped.length > 0) {
          this._auditLogs.set(mapped);
          this._auditTotal.set(result.totalCount ?? mapped.length);
        }
        return mapped.length > 0 ? mapped : this._auditLogs();
      }),
      catchError(() => of(this._auditLogs()))
    );
  }

  rollbackAuditEntry(auditId: string): void {
    this.http.post(`${this.api}audit-logs/${auditId}/revert`, {}).subscribe({
      next: () => {
        this.notify('Alteração revertida.');
        this.loadAuditLogs();
      },
      error: (err) => this.notifyError(err, 'Não foi possível reverter a alteração.'),
    });
  }

  // ── Mensagens ao usuário ──

  private notify(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 4000 });
  }

  /** Shows the API's own message (errors[0].messageCustomer) instead of failing silently. */
  notifyError(err: unknown, fallback: string): void {
    const body = (err as HttpErrorResponse)?.error as ApiErrorResponse | null;
    const first = body?.errors?.[0];
    const message = first?.messageCustomer || first?.message || fallback;
    this.snackBar.open(message, 'Fechar', { duration: 8000, panelClass: 'wfm-snackbar-error' });
  }

  syncRmEmployees(): Observable<BranchOfficeSyncResult> {
    const now = new Date();
    const ts =
      now.toLocaleDateString('pt-BR') +
      ' ' +
      now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    return this.http
      .post<Record<string, unknown>>(`${this.api}employees/sync/rm`, {})
      .pipe(
        catchError((err: HttpErrorResponse) =>
          err.status === 404
            ? this.http.post<Record<string, unknown>>(`${this.api}employees/sync-rm`, {})
            : throwError(() => err)
        ),
        map((res: Record<string, unknown>): BranchOfficeSyncResult => {
          this.loadInitialDataFromApi();
          return {
            inseridas: Number(res?.['inseridas'] ?? res?.['inserted'] ?? 0),
            atualizadas: Number(res?.['atualizadas'] ?? res?.['updated'] ?? 0),
            inalteradas: Number(res?.['inalteradas'] ?? res?.['unchanged'] ?? 0),
            totalProcessadas: Number(res?.['totalProcessadas'] ?? res?.['totalReceived'] ?? this._employees().length),
            timestamp: ts,
            mensagem: String(
              res?.['mensagem'] ||
                res?.['summaryMessage'] ||
                'Sincronização com RM.Api concluída com sucesso.'
            ),
          };
        })
      );
  }

  syncBranchOffice(): Observable<BranchOfficeSyncResult> {
    const now = new Date();
    const ts =
      now.toLocaleDateString('pt-BR') +
      ' ' +
      now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    return this.http.post<Record<string, unknown>>(`${environment.WORKFORCE_API}branches/sync-branchoffice`, {}).pipe(
      map((res: Record<string, unknown>): BranchOfficeSyncResult => {
        const result: BranchOfficeSyncResult = {
          inseridas: Number(res?.['inseridas'] ?? res?.['inserted'] ?? 0),
          atualizadas: Number(res?.['atualizadas'] ?? res?.['updated'] ?? 1),
          inalteradas: Number(res?.['inalteradas'] ?? res?.['unchanged'] ?? 0),
          totalProcessadas: Number(res?.['totalProcessadas'] ?? res?.['totalReceived'] ?? this._branches().length),
          timestamp: ts,
          mensagem: String(
            res?.['mensagem'] ||
              res?.['summaryMessage'] ||
              'Sincronização com o BranchOffice concluída.'
          ),
        };
        this.loadInitialDataFromApi();
        return result;
      }),
    );
  }

  selectDayCurveForHourlyDemand(sigla: OrderHistoryDayCurve['sigla']): void {
    const curve = this.effectiveDayCurves().find((c) => c.sigla === sigla);
    if (!curve) return;
    this._hourlyDemand.update((slots) =>
      slots.map((s) => {
        const pedidos = curve.porHora[s.hora] ?? s.pedidosPrevistos;
        const ideal = Math.max(4, Math.min(16, Math.ceil(pedidos / 4.2) + 3));
        const coz = Math.max(2, Math.round(ideal * 0.46));
        const sal = Math.max(2, ideal - coz);
        return {
          ...s,
          pedidosPrevistos: pedidos,
          staffIdeal: ideal,
          staffCozinhaEscalado: coz,
          staffSalaoEscalado: sal,
          staffTotalEscalado: coz + sal,
          isPico: pedidos >= 35,
        };
      })
    );
  }

  private resolveActiveBranchScaleRule(): ScheduleRuleTemplate | undefined {
    const branch = this.selectedBranch();
    const rules = this._scheduleRules();
    if (!branch) return rules[0];
    return (
      rules.find((r) => r.id === branch.regraEscalaId || r.nomeRegra === branch.regraEscalaNome) ??
      rules[0]
    );
  }

  private computeBreakMinutes(
    ent: number,
    sai: number,
    sInt: number | null,
    rInt: number | null
  ): number {
    if (sInt === null || rInt === null) return 0;
    const effSInt = sInt < ent ? sInt + 1440 : sInt;
    const effRInt = rInt <= effSInt ? rInt + 1440 : rInt;
    if (effSInt > ent && effRInt < sai && effRInt > effSInt) {
      return effRInt - effSInt;
    }
    return 0;
  }

  validateShiftClt(
    entrada: string,
    saidaIntervalo: string,
    retornoIntervalo: string,
    saida: string,
    previousDaySaida?: string,
    consecutiveDaysWorked = 5,
    workedLastThreeSundays = false,
    previousDayEntrada?: string
  ): string[] {
    const rule = this.resolveActiveBranchScaleRule();
    return this.cltRules.validateShiftClt(
      entrada,
      saidaIntervalo,
      retornoIntervalo,
      saida,
      previousDaySaida,
      consecutiveDaysWorked,
      workedLastThreeSundays,
      previousDayEntrada,
      rule
        ? {
            maxHorasSemIntervalo: rule.maxHorasSemIntervalo,
            intervaloMinHoras: rule.intervaloMinHoras,
            intervaloMaxHoras: rule.intervaloMaxHoras,
          }
        : undefined
    );
  }

  computeSmartCltSuggestion(
    entrada: string,
    previousDaySaida?: string,
    consecutiveDaysWorked = 5,
    workedLastThreeSundays = false,
    previousDayEntrada?: string,
    employeeId?: string,
    workDurationMin?: number
  ): {
    recommendFolga: boolean;
    entrada: string;
    saidaIntervalo: string;
    retornoIntervalo: string;
    saida: string;
    summaryLabel: string;
  } {
    const emp = employeeId ? this._employees().find((e) => e.id === employeeId) : undefined;
    const row = employeeId ? this._weeklySchedules().find((r) => r.employeeId === employeeId) : undefined;
    const resolvedWorkMin =
      workDurationMin ||
      emp?.jornadaDiariaMinutos ||
      row?.jornadaDiariaMinutos ||
      (row?.modeloEscala === '5X2' ? 528 : 440);
    const rule = this.resolveActiveBranchScaleRule();
    return this.cltRules.computeSmartCltSuggestion(
      entrada,
      previousDaySaida,
      consecutiveDaysWorked,
      workedLastThreeSundays,
      previousDayEntrada,
      resolvedWorkMin,
      rule
        ? {
            maxHorasSemIntervalo: rule.maxHorasSemIntervalo,
            intervaloMinHoras: rule.intervaloMinHoras,
            intervaloMaxHoras: rule.intervaloMaxHoras,
          }
        : undefined
    );
  }


  private computeRowScheduledHours(shifts: ShiftCell[]): number {
    let totalMin = 0;
    for (const cell of shifts) {
      if (cell.tipo === 'FOLGA' || cell.tipo === 'FERIAS' || cell.tipo === 'AFASTADO') continue;
      const ent = this.toMinHelper(cell.entrada);
      const saiRaw = this.toMinHelper(cell.saida);
      if (ent === null || saiRaw === null) continue;
      const sai = saiRaw <= ent ? saiRaw + 1440 : saiRaw;
      const sInt = this.toMinHelper(cell.saidaIntervalo);
      const rInt = this.toMinHelper(cell.retornoIntervalo);
      const pause = this.computeBreakMinutes(ent, sai, sInt, rInt);
      totalMin += Math.max(0, sai - ent - pause);
    }
    return Math.round((totalMin / 60) * 10) / 10;
  }

  createEmptyWeekRows(
    employees: Employee[] = this._employees(),
    weekDays: WeekDay[] = this.weekDays()
  ): EmployeeWeeklySchedule[] {
    const patterns = this.patternById();
    const shiftTypesCycle: ShiftBadgeType[] = ['ABERTURA', 'INTERMEDIARIO', 'FECHAMENTO'];
    return employees.map((emp, empIdx) => {
      const patternId = Number(emp.regraJornadaId);
      const pattern =
        Number.isInteger(patternId) && patternId > 0 ? patterns.get(patternId) : undefined;
      const { modeloEscala, folgasSemanaisPadrao, folgasSemanaisObrigatorias, horasContratuais } =
        resolveScaleDefaults(
          emp.regraJornadaNome,
          emp.horasContratuaisSemanais,
          pattern,
          emp.jornadaDiariaMinutos
        );
      const isFemale = (emp.genero || '').toUpperCase().startsWith('F');
      const hasSundayOffThisWeek =
        modeloEscala === '6X1' &&
        ((emp.domingosTrabalhadosConsecutivos ?? 0) >= (isFemale ? 1 : 3) || empIdx === 0);
      const offDayIndex = (empIdx % 6) + 1;
      const defaultTipo = shiftTypesCycle[empIdx % shiftTypesCycle.length];
      const presetByTipo: Record<
        'ABERTURA' | 'INTERMEDIARIO' | 'FECHAMENTO',
        { entrada: string; saidaIntervalo: string; retornoIntervalo: string; saida: string }
      > = {
        ABERTURA: { entrada: '09:30', saidaIntervalo: '13:30', retornoIntervalo: '14:50', saida: '18:10' },
        INTERMEDIARIO: { entrada: '12:00', saidaIntervalo: '15:30', retornoIntervalo: '16:50', saida: '20:40' },
        FECHAMENTO: { entrada: '15:20', saidaIntervalo: '18:30', retornoIntervalo: '19:50', saida: '00:00' },
      };
      const preset = presetByTipo[defaultTipo as 'ABERTURA' | 'INTERMEDIARIO' | 'FECHAMENTO'] ?? presetByTipo.ABERTURA;
      const shifts: ShiftCell[] = weekDays.map((day) => {
        const isOff = day.index === offDayIndex || (hasSundayOffThisWeek && day.index === 0);
        return {
          dayIndex: day.index,
          dayLabel: day.label,
          dateStr: day.dateStr,
          dataIso: day.iso,
          tipo: isOff ? 'FOLGA' : defaultTipo,
          entrada: isOff ? 'FOLGA' : preset.entrada,
          saidaIntervalo: isOff ? '-' : preset.saidaIntervalo,
          retornoIntervalo: isOff ? '-' : preset.retornoIntervalo,
          saida: isOff ? 'FOLGA' : preset.saida,
          editadoManualmente: false,
          editadoPosPublicacao: false,
          cltAlertas: [],
        };
      });
      const folgasSemana = shifts.filter((c) => c.tipo === 'FOLGA').length;
      const domFolgaSemana = shifts.some((c) => c.dayIndex === 0 && c.tipo === 'FOLGA') ? 1 : 0;
      const effectiveHoras = resolveEffectiveWeeklyHours(modeloEscala, horasContratuais, shifts);
      const resolvedTags =
        emp.setorTags && emp.setorTags.length > 0
          ? emp.setorTags
          : emp.setoresTags && emp.setoresTags.length > 0
            ? emp.setoresTags
            : [emp.setor];
      return {
        employeeId: emp.id,
        chapa: emp.chapa,
        cpf: emp.cpf,
        genero: emp.genero,
        nome: emp.nome,
        funcao: emp.funcao,
        cargoNivel: emp.cargoNivel,
        setor: emp.setor,
        setorTags: resolvedTags,
        setoresTags: resolvedTags,
        categoriaFuncao: emp.categoriaFuncao,
        contabilizaProducaoPedidos: emp.contabilizaProducaoPedidos,
        situacaoReal: emp.situacaoReal,
        codFilial: emp.codFilial,
        nomeFilial: emp.nomeFilial,
        regraJornadaNome: emp.regraJornadaNome,
        modeloEscala,
        folgasSemanaisPadrao,
        folgasSemanaisObrigatorias,
        jornadaDiariaMinutos: emp.jornadaDiariaMinutos ?? pattern?.dailyWorkloadMinutes ?? 440,
        horasContratuais: effectiveHoras,
        horasEscaladas: this.computeRowScheduledHours(shifts),
        folgasMesAtual: folgasSemana,
        folgasMesPrevistas: modeloEscala === '6X1' ? (isFemale ? 6 : 5) : folgasSemanaisPadrao * 4,
        domingosFolgaMesAtual: domFolgaSemana,
        domingosFolgaMesPrevistos: isFemale ? 2 : 1,
        shifts,
      };
    });
  }

  private revalidateEmployeeWeekAlerts(row: EmployeeWeeklySchedule): EmployeeWeeklySchedule {
    if (!row.shifts.some((c) => c.cltAlertas && c.cltAlertas.length > 0)) {
      return row;
    }

    const modelo = (row.modeloEscala || '6X1').toUpperCase();
    const requiredFolgas = row.folgasSemanaisObrigatorias ?? (modelo === '5X2' ? 2 : 1);
    const folgaCells = row.shifts.filter(
      (s) => s.tipo === 'FOLGA' || s.tipo === 'FERIAS' || s.tipo === 'AFASTADO'
    );
    const folgasCount = folgaCells.length;
    const hasSundayOff = folgaCells.some((s) => s.dayIndex === 0);
    const isFemale = (row.genero || '').toUpperCase().startsWith('F');
    const maxFolgasMonth = row.folgasMesPrevistas ?? (modelo === '5X2' ? 8 : isFemale ? 6 : 5);
    const currentFolgasMonth = row.folgasMesAtual ?? folgasCount;
    const maxSundaysOffMonth = row.domingosFolgaMesPrevistos ?? (isFemale ? 2 : 1);
    const currentSundaysOffMonth = row.domingosFolgaMesAtual ?? (hasSundayOff ? 1 : 0);
    const hasMonthlySundayExcess = hasSundayOff && currentSundaysOffMonth > maxSundaysOffMonth;
    const isValidFolgaCount =
      modelo === '6X1'
        ? folgasCount === 1 ||
          (folgasCount === 2 &&
            hasSundayOff &&
            currentFolgasMonth <= maxFolgasMonth &&
            currentSundaysOffMonth <= maxSundaysOffMonth)
        : folgasCount === requiredFolgas;
    const workCount = row.shifts.filter(
      (s) => s.tipo !== 'FOLGA' && s.tipo !== 'FERIAS' && s.tipo !== 'AFASTADO'
    ).length;
    const totalWeekHours = this.computeRowScheduledHours(row.shifts);
    const effectiveMaxHours = resolveEffectiveWeeklyHours(
      modelo,
      row.horasContratuais,
      row.shifts
    );
    const hasWeeklyHoursExcess =
      effectiveMaxHours > 0 && Math.round(totalWeekHours * 10) / 10 > effectiveMaxHours;

    const revalidatedShifts = row.shifts.map((cell) => {
      if (!cell.cltAlertas || cell.cltAlertas.length === 0) {
        return cell;
      }

      if (cell.tipo === 'FERIAS' || cell.tipo === 'AFASTADO') {
        return { ...cell, cltAlertas: [] };
      }

      if (cell.tipo === 'FOLGA') {
        const nextAlerts: string[] = [];
        if (modelo === '5X2' && cell.dayIndex === 6) {
          nextAlerts.push(
            'No modelo de escala 5X2, as folgas semanais devem ocorrer de segunda a sexta-feira (ou 1 folga seg-sex + 1 domingo do mês), não permitindo folga regular no sábado.'
          );
        }
        if (cell.dayIndex === 0 && hasMonthlySundayExcess) {
          nextAlerts.push(
            `Regra ${modelo}: o colaborador está com ${currentSundaysOffMonth} domingo(s) de folga no mês, acima do limite parametrizado (${maxSundaysOffMonth} domingo(s)/mês).`
          );
        }
        if (!isValidFolgaCount) {
          nextAlerts.push(
            `O modelo de escala ${modelo} exige exatamente ${requiredFolgas} folga(s) semanal(is).`
          );
        }
        return { ...cell, cltAlertas: nextAlerts };
      }

      const prevCell =
        cell.dayIndex > 0
          ? row.shifts.find((s) => s.dayIndex === cell.dayIndex - 1)
          : undefined;
      const prevSaida =
        prevCell &&
        prevCell.tipo !== 'FOLGA' &&
        prevCell.tipo !== 'FERIAS' &&
        prevCell.tipo !== 'AFASTADO' &&
        prevCell.saida?.includes(':')
          ? prevCell.saida
          : undefined;
      const prevEntrada =
        prevCell &&
        prevCell.tipo !== 'FOLGA' &&
        prevCell.tipo !== 'FERIAS' &&
        prevCell.tipo !== 'AFASTADO' &&
        prevCell.entrada?.includes(':')
          ? prevCell.entrada
          : undefined;

      const nextAlerts = [
        ...this.validateShiftClt(
          cell.entrada,
          cell.saidaIntervalo,
          cell.retornoIntervalo,
          cell.saida,
          prevSaida,
          workCount,
          false,
          prevEntrada
        ),
      ];

      if (cell.dayIndex < 6) {
        const nextCell = row.shifts.find((s) => s.dayIndex === cell.dayIndex + 1);
        if (
          nextCell &&
          nextCell.tipo !== 'FOLGA' &&
          nextCell.tipo !== 'FERIAS' &&
          nextCell.tipo !== 'AFASTADO' &&
          nextCell.entrada?.includes(':')
        ) {
          const nextDayAlerts = this.validateShiftClt(
            nextCell.entrada,
            nextCell.saidaIntervalo,
            nextCell.retornoIntervalo,
            nextCell.saida,
            cell.saida,
            workCount,
            false,
            cell.entrada
          );
          const interShiftAlert = nextDayAlerts.find(
            (a) => a.toLowerCase().includes('interjornada') || a.toLowerCase().includes('art. 66')
          );
          if (interShiftAlert) {
            nextAlerts.push(
              `Conflito com ${nextCell.dayLabel} (${nextCell.dateStr}): ${interShiftAlert}`
            );
          }
        }
      }

      if (folgasCount < requiredFolgas) {
        nextAlerts.push(
          `O colaborador está com ${folgasCount} folga(s) na semana, violando o modelo contratual ${modelo} (mínimo de ${requiredFolgas} folga(s)/semana).`
        );
      }

      if (hasWeeklyHoursExcess) {
        nextAlerts.push(
          `A carga semanal projetada (${totalWeekHours.toFixed(1)}h) ultrapassa o limite contratual (${effectiveMaxHours}h).`
        );
      }

      return { ...cell, cltAlertas: nextAlerts };
    });

    return {
      ...row,
      horasContratuais: effectiveMaxHours,
      shifts: revalidatedShifts,
    };
  }

  private syncRowMonthlyCountersAndHours(
    sched: EmployeeWeeklySchedule,
    nextShifts: ShiftCell[]
  ): EmployeeWeeklySchedule {
    const prevFolgas = sched.shifts.filter((c) => c.tipo === 'FOLGA').length;
    const nextFolgas = nextShifts.filter((c) => c.tipo === 'FOLGA').length;
    const prevDomFolga = sched.shifts.some((c) => c.dayIndex === 0 && c.tipo === 'FOLGA') ? 1 : 0;
    const nextDomFolga = nextShifts.some((c) => c.dayIndex === 0 && c.tipo === 'FOLGA') ? 1 : 0;
    const folgasMesAtual = Math.max(0, (sched.folgasMesAtual ?? prevFolgas) + (nextFolgas - prevFolgas));
    const domingosFolgaMesAtual = Math.max(
      0,
      (sched.domingosFolgaMesAtual ?? prevDomFolga) + (nextDomFolga - prevDomFolga)
    );
    return this.revalidateEmployeeWeekAlerts({
      ...sched,
      folgasMesAtual,
      domingosFolgaMesAtual,
      horasContratuais: resolveEffectiveWeeklyHours(
        sched.modeloEscala,
        sched.horasContratuais,
        nextShifts
      ),
      horasEscaladas: this.computeRowScheduledHours(nextShifts),
      shifts: nextShifts,
    });
  }

  updateEmployeeShift(
    employeeId: string,
    dayIndex: number,
    updatedShift: Partial<ShiftCell>,
    justificativa: string
  ): void {
    const before = this._weeklySchedules();
    const isPublished = this.isSchedulePublished();
    const previousTargetCell = before
      .find((s) => s.employeeId === employeeId)
      ?.shifts.find((c) => c.dayIndex === dayIndex);

    this._weeklySchedules.update((schedules) =>
      schedules.map((sched) => {
        if (sched.employeeId !== employeeId) return sched;
        const nextShifts = sched.shifts.map((cell) =>
          cell.dayIndex !== dayIndex
            ? cell
            : {
                ...cell,
                ...updatedShift,
                editadoManualmente: true,
                editadoPosPublicacao:
                  isPublished || Boolean(updatedShift.editadoPosPublicacao || cell.editadoPosPublicacao),
                justificativa,
              }
        );
        return this.syncRowMonthlyCountersAndHours(sched, nextShifts);
      })
    );

    // Se houve permuta guiada de folga (Item 2.1 / 2.2) em qualquer sentido (Trabalho -> Folga ou Folga -> Trabalho), reflete na célula permutada imediatamente na UI
    const swapIdx = updatedShift.permutarFolgaDiaIndex;
    if (swapIdx !== undefined && swapIdx !== null && swapIdx !== dayIndex && previousTargetCell) {
      const isConvertingFolgaToWork =
        previousTargetCell.tipo === 'FOLGA' &&
        updatedShift.tipo !== undefined &&
        updatedShift.tipo !== 'FOLGA';

      const fallbackTipo =
        previousTargetCell.tipo !== 'FOLGA' &&
        previousTargetCell.tipo !== 'FERIAS' &&
        previousTargetCell.tipo !== 'AFASTADO'
          ? previousTargetCell.tipo
          : 'ABERTURA';

      this._weeklySchedules.update((schedules) =>
        schedules.map((sched) => {
          if (sched.employeeId !== employeeId) return sched;
          const nextShifts = sched.shifts.map((cell) => {
            if (cell.dayIndex === swapIdx) {
              if (isConvertingFolgaToWork) {
                return {
                  ...cell,
                  tipo: 'FOLGA' as ShiftBadgeType,
                  entrada: 'FOLGA',
                  saidaIntervalo: '-',
                  retornoIntervalo: '-',
                  saida: 'FOLGA',
                  editadoManualmente: true,
                  editadoPosPublicacao: isPublished || Boolean(cell.editadoPosPublicacao),
                  justificativa: `Permuta de folga semanal com ${previousTargetCell.dayLabel}`,
                  cltAlertas: [],
                };
              }
              return {
                ...cell,
                tipo: fallbackTipo,
                entrada: previousTargetCell.entrada?.includes(':') ? previousTargetCell.entrada : '09:30',
                saidaIntervalo: previousTargetCell.saidaIntervalo?.includes(':') ? previousTargetCell.saidaIntervalo : '13:30',
                retornoIntervalo: previousTargetCell.retornoIntervalo?.includes(':') ? previousTargetCell.retornoIntervalo : '14:50',
                saida: previousTargetCell.saida?.includes(':') ? previousTargetCell.saida : '18:10',
                editadoManualmente: true,
                editadoPosPublicacao: isPublished || Boolean(cell.editadoPosPublicacao),
                justificativa: `Permuta de folga semanal com ${previousTargetCell.dayLabel}`,
                cltAlertas: [],
              };
            }
            return cell;
          });
          return this.syncRowMonthlyCountersAndHours(sched, nextShifts);
        })
      );
    }

    this.persistScheduleChanges(before, justificativa);
  }

  updateEmployeeStatus(employeeId: string, novoStatus: Employee['situacaoReal']): void {
    const emp = this._employees().find((e) => e.id === employeeId);
    const original = this.apiEmployees.get(Number(employeeId));
    if (!emp || !original) return;

    const body = toEmployeePayload({ ...emp, situacaoReal: novoStatus }, original, original.branchId);
    this.http.put<ApiEmployee>(`${this.api}employees/${original.id}`, body).subscribe({
      next: (saved) => this.applySavedEmployee(saved),
      error: (err) => this.notifyError(err, 'Não foi possível alterar a situação do colaborador.'),
    });
  }

  private applySavedEmployee(saved: ApiEmployee): void {
    this.apiEmployees.set(saved.id, saved);
    const mapped = this.mapEmployee(saved);
    this._employees.update((list) =>
      list.some((e) => e.id === mapped.id)
        ? list.map((e) => (e.id === mapped.id ? mapped : e))
        : [mapped, ...list]
    );
  }

  /** Scale model of a row: taken from the work schedule pattern when known, otherwise inferred from its name. */
  private resolveScaleModelMeta(
    regraJornadaNome: string,
    customHours?: number,
    patternId?: number,
    customDailyMinutes?: number
  ): {
    modeloEscala: string;
    folgasSemanaisPadrao: number;
    folgasSemanaisObrigatorias: number;
    horasContratuais: number;
  } {
    const pattern = patternId != null ? this.patternById().get(patternId) : undefined;
    const defaults = resolveScaleDefaults(regraJornadaNome, customHours, pattern, customDailyMinutes);
    return {
      modeloEscala: defaults.modeloEscala,
      folgasSemanaisPadrao: defaults.folgasSemanaisPadrao,
      folgasSemanaisObrigatorias: defaults.folgasSemanaisObrigatorias,
      horasContratuais: defaults.horasContratuais,
    };
  }

  /** Re-maps the given employees from their API records and refreshes their rows in the week grid. */
  private applyEmployeesFromApi(ids: ReadonlySet<number>): void {
    const updated = new Map<string, Employee>();
    for (const id of ids) {
      const raw = this.apiEmployees.get(id);
      if (raw) updated.set(String(id), this.mapEmployee(raw));
    }
    if (updated.size === 0) return;

    this._employees.update((list) => list.map((e) => updated.get(e.id) ?? e));
    this._weeklySchedules.update((rows) =>
      rows.map((row) => {
        const emp = updated.get(row.employeeId);
        if (!emp) return row;
        const patternId = Number(emp.regraJornadaId);
        const meta = this.resolveScaleModelMeta(
          emp.regraJornadaNome,
          emp.horasContratuaisSemanais,
          Number.isInteger(patternId) && patternId > 0 ? patternId : undefined,
          emp.jornadaDiariaMinutos
        );
        const tags = emp.setorTags?.length ? emp.setorTags : [emp.setor];
        return {
          ...row,
          regraJornadaNome: emp.regraJornadaNome,
          modeloEscala: meta.modeloEscala,
          folgasSemanaisPadrao: meta.folgasSemanaisPadrao,
          folgasSemanaisObrigatorias: meta.folgasSemanaisPadrao,
          jornadaDiariaMinutos: emp.jornadaDiariaMinutos,
          horasContratuais: meta.horasContratuais,
          genero: emp.genero,
          setor: emp.setor,
          setorTags: tags,
          setoresTags: tags,
        };
      })
    );
  }

  /**
   * PATCH employees/batch. The screen changes only after the API confirms; on error nothing is
   * applied and the caller shows the API message.
   */
  batchAssignEmployees(employeeIds: string[], changes: EmployeeBatchChanges): Observable<number> {
    const numericIds = employeeIds.map(Number).filter((n) => Number.isInteger(n) && n > 0);
    if (numericIds.length === 0) return of(0);

    const pattern = changes.regraJornadaId != null ? this.patternById().get(changes.regraJornadaId) : undefined;
    const cleanTags = (changes.setorTags ?? []).map(normalizeSectorTag).filter(Boolean);
    const body: ApiEmployeeBatchUpdate = {
      employeeIds: numericIds,
      workSchedulePatternId: pattern?.id,
      workScheduleName: pattern?.name,
      contractWeeklyHours: changes.horasContratuaisSemanais,
      customDailyWorkloadMinutes: changes.jornadaDiariaMinutos,
      gender: changes.genero,
      sectorTags: cleanTags.length > 0 ? cleanTags.join(',') : undefined,
    };

    return this.http.patch<ApiEmployee[] | null>(`${this.api}employees/batch`, body).pipe(
      map((saved) => {
        const savedList = Array.isArray(saved) ? saved : [];
        if (savedList.length > 0) {
          savedList.forEach((s) => this.apiEmployees.set(s.id, s));
        } else {
          // Answer without body: align the raw records with what the API accepted.
          for (const id of numericIds) {
            const raw = this.apiEmployees.get(id);
            if (!raw) continue;
            this.apiEmployees.set(id, {
              ...raw,
              workSchedulePatternId: body.workSchedulePatternId ?? raw.workSchedulePatternId,
              rawScheduleName: body.workScheduleName ?? raw.rawScheduleName,
              contractWeeklyHours: body.contractWeeklyHours ?? raw.contractWeeklyHours,
              customDailyWorkloadMinutes: body.customDailyWorkloadMinutes ?? raw.customDailyWorkloadMinutes,
              gender: body.gender ?? raw.gender,
              sectorTags: body.sectorTags ?? raw.sectorTags,
            });
          }
        }
        this.applyEmployeesFromApi(new Set(numericIds));
        return numericIds.length;
      })
    );
  }

  /** PUT employees/{id} with the contract, pattern, gender and tags; the screen changes only after success. */
  updateEmployeeContractAndRules(employeeId: string, changes: EmployeeContractChanges): Observable<Employee> {
    const emp = this._employees().find((e) => e.id === employeeId);
    const original = this.apiEmployees.get(Number(employeeId));
    if (!emp || !original) {
      return throwError(() => new Error('Colaborador não encontrado na filial selecionada.'));
    }

    const pattern = changes.regraJornadaId != null ? this.patternById().get(changes.regraJornadaId) : undefined;
    const cleanTags = changes.setorTags.map(normalizeSectorTag).filter(Boolean);
    const draft: Employee = {
      ...emp,
      regraJornadaId: pattern ? String(pattern.id) : emp.regraJornadaId,
      regraJornadaNome: pattern?.name ?? emp.regraJornadaNome,
      horasContratuaisSemanais: changes.horasContratuaisSemanais ?? undefined,
      jornadaDiariaMinutos: changes.jornadaDiariaMinutos ?? undefined,
      genero: changes.genero,
      setorTags: cleanTags.length > 0 ? cleanTags : emp.setorTags,
      setoresTags: cleanTags.length > 0 ? cleanTags : emp.setoresTags,
    };
    const body = toEmployeePayload(draft, original, original.branchId);
    body.contractWeeklyHours = changes.horasContratuaisSemanais ?? null;
    body.customDailyWorkloadMinutes = changes.jornadaDiariaMinutos ?? null;

    return this.http.put<ApiEmployee | null>(`${this.api}employees/${original.id}`, body).pipe(
      map((saved) => {
        this.apiEmployees.set(original.id, saved?.id ? saved : { ...original, ...body, id: original.id });
        this.applyEmployeesFromApi(new Set([original.id]));
        return this._employees().find((e) => e.id === employeeId) ?? draft;
      })
    );
  }

  // ── Tags de setor ──

  /** Adds a tag typed by the user to the catalog (persisted when applied to an employee). */
  addCustomSectorTag(raw: string): SectorTagValidation {
    const result = validateNewSectorTag(raw, this.sectorTagCatalog());
    if (result.ok) {
      this._customSectorTags.update((list) => [...list, result.tag]);
    }
    return result;
  }

  // ── Funções do RM (job-roles) ──

  /** PUT job-roles/{id} (or POST for a new role). Admin only in the API. */
  saveRoleMapping(role: RoleMapping): Observable<RoleMapping> {
    const apiId = Number(role.id);
    const original = Number.isInteger(apiId) && apiId > 0 ? this.jobRoleById().get(apiId) : undefined;
    const body = toJobRoleWrite(role, original);
    const request$ = original
      ? this.http.put<ApiJobRole>(`${this.api}job-roles/${original.id}`, body)
      : this.http.post<ApiJobRole>(`${this.api}job-roles`, body);

    return request$.pipe(
      map((saved) => {
        const stored: ApiJobRole = saved?.id ? saved : { ...original, ...body, id: apiId };
        this._jobRoles.update((list) =>
          list.some((r) => r.id === stored.id)
            ? list.map((r) => (r.id === stored.id ? stored : r))
            : [...list, stored]
        );
        this.refreshEmployeesFromApi();
        return toRoleMapping(stored);
      })
    );
  }

  /** POST job-roles/sync/rm: brings the RM functions and links them to the standard roles. Admin only. */
  syncJobRolesFromRm(): Observable<ApiJobRoleSyncResult> {
    return this.http.post<ApiJobRoleSyncResult>(`${this.api}job-roles/sync/rm`, {}).pipe(
      switchMap((result) =>
        Array.isArray(result?.roles)
          ? of(result)
          : this.http.get<ApiJobRole[]>(`${this.api}job-roles`).pipe(map((roles) => ({ ...result, roles })))
      ),
      tap((result) => {
        this._jobRoles.set(result.roles ?? []);
        this.refreshEmployeesFromApi();
      })
    );
  }

  // ── Jornadas (work-schedule-patterns) ──

  /** PUT work-schedule-patterns/{id} for an existing pattern, POST otherwise. Admin only in the API. */
  saveJourneyRule(rule: JourneyRule): Observable<JourneyRule> {
    const apiId = Number(rule.id);
    const exists = Number.isInteger(apiId) && apiId > 0 && this.patternById().has(apiId);
    const body = toWorkSchedulePatternWrite(rule);
    const request$ = exists
      ? this.http.put<ApiWorkSchedulePattern>(`${this.api}work-schedule-patterns/${apiId}`, body)
      : this.http.post<ApiWorkSchedulePattern>(`${this.api}work-schedule-patterns`, body);

    return request$.pipe(
      map((saved) => {
        const stored: ApiWorkSchedulePattern = saved?.id ? saved : { ...body, id: apiId };
        this._schedulePatterns.update((list) =>
          list.some((p) => p.id === stored.id)
            ? list.map((p) => (p.id === stored.id ? stored : p))
            : [...list, stored]
        );
        this.refreshEmployeesFromApi();
        return toJourneyRule(stored);
      })
    );
  }

  deleteJourneyRule(ruleId: string): Observable<void> {
    const apiId = Number(ruleId);
    return this.http.delete<void>(`${this.api}work-schedule-patterns/${apiId}`).pipe(
      tap(() => this._schedulePatterns.update((list) => list.filter((p) => p.id !== apiId)))
    );
  }

  saveBranchOperation(branch: BranchOperation): void {
    const id = Number(branch.id);
    const original = this.apiBranches.get(id);
    const body = { ...original, ...toBranchPayload(branch) };
    const request$ = original
      ? this.http.put<ApiBranch>(`${this.api}branches/${id}`, body)
      : this.http.post<ApiBranch>(`${this.api}branches`, body);

    request$.subscribe({
      next: (saved) => {
        this.apiBranches.set(saved.id, saved);
        const mapped = toBranchOperation(saved, this._scheduleRules());
        const mergedBranch: BranchOperation = {
          ...mapped,
          openingPrepFloorHours: branch.openingPrepFloorHours ?? mapped.openingPrepHours,
          closingPrepFloorHours: branch.closingPrepFloorHours ?? mapped.closingPrepHours,
        };
        this._branches.update((list) =>
          list.some((b) => b.id === mergedBranch.id)
            ? list.map((b) =>
                b.id === mergedBranch.id
                  ? {
                      ...mergedBranch,
                      escalaPublicada: b.escalaPublicada,
                      eficienciaMedia: b.eficienciaMedia,
                      quadroIdeal: b.quadroIdeal,
                    }
                  : b
              )
            : [mergedBranch, ...list]
        );
        this.notify('Filial salva.');
      },
      error: (err) => this.notifyError(err, 'Não foi possível salvar a filial.'),
    });
  }

  batchUpdateDirector(oldCode: string, newCode: string, newName: string): number {
    const affected = this._branches().filter((b) => b.codDiretor === oldCode).length;
    const body: ApiBatchDirectorUpdate = {
      currentDirectorCode: oldCode,
      newDirectorCode: newCode,
      newDirectorName: newName,
    };
    this.http.patch(`${this.api}branches/batch/director`, body).subscribe({
      next: () =>
        this._branches.update((list) =>
          list.map((b) => (b.codDiretor === oldCode ? { ...b, codDiretor: newCode, nomeDiretor: newName } : b))
        ),
      error: (err) => this.notifyError(err, 'Não foi possível trocar o diretor das filiais.'),
    });
    return affected;
  }

  batchUpdateRegionalManager(oldCode: string, newCode: string, newName: string): number {
    const affected = this._branches().filter((b) => b.codGoOp === oldCode).length;
    const body: ApiBatchRegionalManagerUpdate = {
      currentRegionalManagerCode: oldCode,
      newRegionalManagerCode: newCode,
      newRegionalManagerName: newName,
    };
    this.http.patch(`${this.api}branches/batch/regional-manager`, body).subscribe({
      next: () =>
        this._branches.update((list) =>
          list.map((b) => (b.codGoOp === oldCode ? { ...b, codGoOp: newCode, nomeGoOp: newName } : b))
        ),
      error: (err) => this.notifyError(err, 'Não foi possível trocar o gerente regional das filiais.'),
    });
    return affected;
  }

  /**
   * Saves a scale rule in Workforce.Api (PUT when it already exists there, POST otherwise)
   * and updates the local list only after the API confirms.
   */
  saveScheduleRule(rule: ScheduleRuleTemplate): Observable<unknown> {
    const body = {
      name: rule.nomeRegra,
      segment: rule.segmentoAlvo,
      maxConsecutiveDays: rule.maxDiasConsecutivos,
      maxConsecutiveHoursNoInterval: rule.maxHorasSemIntervalo,
      minIntervalHours: rule.intervaloMinHoras,
      maxIntervalHours: rule.intervaloMaxHoras,
      minRestBetweenShiftsHours: rule.descansoInterjornadaHoras,
      weeklyHoursLimit: rule.cargaSemanalHoras,
      sundayOffWeeklyHours: rule.cargaComFolgaDomHoras,
      openingPrepKitchenHours: rule.aberturaCozinhaHoras,
      openingPrepFloorHours: rule.aberturaSalaoHoras,
      closingPrepKitchenHours: rule.fechamentoCozinhaHoras,
      closingPrepFloorHours: rule.fechamentoSalaoHoras,
      minLeadershipCount: rule.liderancaMinimaPresencial,
      minPrepKitchenCount: rule.qtdAberturaCozinhaSth,
      minPrepFloorCount: rule.qtdAberturaSalaoSth,
      minPrepAuxiliaryCount: rule.qtdAberturaAuxiliarCtnJer,
      avoidEntryExitPeak: rule.evitarEntradaSaidaPico,
      minStaffOpen: rule.coberturaOperacionalMinima,
      defaultMonthlySundaysOff: rule.domingosFolgaPadraoMes ?? 1,
      femaleMonthlySundaysOff: rule.domingosFolgaMulheresMes ?? 2,
      isDefault: rule.isDefault ?? false,
    };
    const apiId = Number(rule.id);
    const request$ = Number.isInteger(apiId) && apiId > 0
      ? this.http.put<ApiScaleRule>(`${this.api}scale-rules/${apiId}`, body)
      : this.http.post<ApiScaleRule>(`${this.api}scale-rules`, body);

    return request$.pipe(
      tap((saved) => {
        const mapped = saved?.id ? toScheduleRule(saved) : rule;
        this._scheduleRules.update((list) =>
          list.some((r) => r.id === rule.id)
            ? list.map((r) => (r.id === rule.id ? mapped : r))
            : [...list, mapped]
        );
      })
    );
  }

  createScheduleRule(rule: Omit<ScheduleRuleTemplate, 'id'>): Observable<ScheduleRuleTemplate> {
    const body = {
      name: rule.nomeRegra,
      segment: rule.segmentoAlvo,
      maxConsecutiveDays: rule.maxDiasConsecutivos,
      maxConsecutiveHoursNoInterval: rule.maxHorasSemIntervalo,
      minIntervalHours: rule.intervaloMinHoras,
      maxIntervalHours: rule.intervaloMaxHoras,
      minRestBetweenShiftsHours: rule.descansoInterjornadaHoras,
      weeklyHoursLimit: rule.cargaSemanalHoras,
      sundayOffWeeklyHours: rule.cargaComFolgaDomHoras,
      openingPrepKitchenHours: rule.aberturaCozinhaHoras,
      openingPrepFloorHours: rule.aberturaSalaoHoras,
      closingPrepKitchenHours: rule.fechamentoCozinhaHoras,
      closingPrepFloorHours: rule.fechamentoSalaoHoras,
      minLeadershipCount: rule.liderancaMinimaPresencial,
      minPrepKitchenCount: rule.qtdAberturaCozinhaSth,
      minPrepFloorCount: rule.qtdAberturaSalaoSth,
      minPrepAuxiliaryCount: rule.qtdAberturaAuxiliarCtnJer,
      avoidEntryExitPeak: rule.evitarEntradaSaidaPico,
      minStaffOpen: rule.coberturaOperacionalMinima,
      defaultMonthlySundaysOff: rule.domingosFolgaPadraoMes ?? 1,
      femaleMonthlySundaysOff: rule.domingosFolgaMulheresMes ?? 2,
      isDefault: rule.isDefault ?? false,
    };
    return this.http.post<ApiScaleRule>(`${this.api}scale-rules`, body).pipe(
      map((saved) => {
        const fallbackId = String(saved?.id ?? Date.now());
        const mapped: ScheduleRuleTemplate = saved?.id
          ? toScheduleRule(saved)
          : { ...rule, id: fallbackId };
        this._scheduleRules.update((list) => [...list, mapped]);
        return mapped;
      })
    );
  }

  deleteScheduleRule(ruleId: string): Observable<void> {
    const apiId = Number(ruleId);
    return this.http.delete<void>(`${this.api}scale-rules/${apiId}`).pipe(
      tap(() => this._scheduleRules.update((list) => list.filter((r) => r.id !== String(ruleId))))
    );
  }

  addEmployee(emp: Employee): void {
    const branchId = this._selectedBranchId();
    if (branchId == null) {
      this.notify('Selecione uma filial antes de cadastrar o colaborador.');
      return;
    }
    this.http.post<ApiEmployee>(`${this.api}employees`, toEmployeePayload(emp, undefined, branchId)).subscribe({
      next: (saved) => {
        this.applySavedEmployee(saved);
        this.notify(`Colaborador ${saved.fullName} cadastrado.`);
      },
      error: (err) => this.notifyError(err, 'Não foi possível cadastrar o colaborador.'),
    });
  }

  addVacation(vac: VacationRecord): void {
    const emp = this._employees().find((e) => e.chapa === vac.chapa);
    const branchId = this._selectedBranchId();
    if (!emp || branchId == null) {
      this.notify('Colaborador não encontrado na filial selecionada.');
      return;
    }
    const rawObs = (vac.motivo || '').trim();
    const taggedObs =
      vac.origem === 'MANUAL' && !rawObs.startsWith('[MANUAL]')
        ? `[MANUAL] ${rawObs}`.trim()
        : rawObs;
    const body: Partial<ApiEmployeeAbsence> = {
      employeeId: Number(emp.id),
      branchId,
      absenceType: fromVacationType(vac.tipo),
      startDate: vac.dataInicio,
      endDate: vac.dataFim,
      daysCount: vac.diasTotais,
      observation: taggedObs,
    };
    this.http.post<ApiEmployeeAbsence>(`${this.api}employee-absences`, body).subscribe({
      next: (saved) => {
        this._vacations.update((list) => [{ ...toVacationRecord(saved, emp), origem: 'MANUAL' }, ...list]);
        this.notify('Afastamento registrado. Gere a escala da semana de novo para considerá-lo.');
      },
      error: (err) => this.notifyError(err, 'Não foi possível registrar o afastamento.'),
    });
  }

  addHoliday(holiday: HolidayEvent): void {
    const scopeType =
      holiday.escopo === 'ESTADUAL' ? 'State' : holiday.escopo === 'MUNICIPAL' ? 'City' : 'National';
    this.http
      .post<ApiHolidayEvent>(`${this.api}holiday-events`, {
        name: holiday.nome,
        eventDate: holiday.data,
        scopeType,
        impactFactor: holiday.impactFactorMultiplier ?? 1 + holiday.fatorImpactoPct / 100,
        isRecurring: Boolean(holiday.recorrenteFixo ?? holiday.categoriaData === 'FIXO'),
      })
      .subscribe({
        next: (saved) => this._holidays.update((list) => [toHoliday(saved), ...list]),
        error: (err) => this.notifyError(err, 'Não foi possível cadastrar o feriado.'),
      });
  }

  generateHolidayCalendar(year: number): Observable<unknown> {
    return this.http
      .post<ApiHolidayEvent[]>(`${this.api}holiday-events/generate-year/${year}`, {})
      .pipe(tap(() => this.loadInitialDataFromApi()));
  }

  optimizeHourlySchedule(): void {
    this._hourlyDemand.update((slots) =>
      slots.map((s) => {
        const idealCoz = Math.max(2, Math.round(s.staffIdeal * 0.48));
        const idealSal = Math.max(2, s.staffIdeal - idealCoz);
        return {
          ...s,
          staffCozinhaEscalado: idealCoz,
          staffSalaoEscalado: idealSal,
          staffTotalEscalado: idealCoz + idealSal,
        };
      })
    );
  }

  /** Opens a ticket in the selected branch. Code, requester and status are set by the API. */
  createCorrectionTicket(
    ticket: Omit<TimecardCorrectionTicket, 'id' | 'ticketCode' | 'status' | 'criadoEm'>
  ): void {
    const emp = this._employees().find((e) => e.chapa === ticket.chapa);
    const branchId = this._selectedBranchId();
    if (!emp || branchId == null) {
      this.notify('Colaborador não encontrado na filial selecionada.');
      return;
    }
    const justification = [
      ticket.justificativaGestor.trim(),
      ticket.horarioPrevisto && `Previsto: ${ticket.horarioPrevisto}`,
      ticket.horarioRealizado && `Realizado: ${ticket.horarioRealizado}`,
      ticket.horarioSolicitado && `Solicitado: ${ticket.horarioSolicitado}`,
    ]
      .filter(Boolean)
      .join('\n');

    const body: Partial<ApiTimecardTicket> = {
      branchId,
      employeeId: Number(emp.id),
      employeeName: emp.nome,
      employeeCode: emp.chapa,
      occurrenceDate: ticket.dataOcorrencia,
      reasonCategory: ticket.tipoAjuste,
      justification,
      messages: [{ messageText: ticket.justificativaGestor.trim() }],
    };
    this.http.post<ApiTimecardTicket>(`${this.api}timecard-tickets`, body).subscribe({
      next: (saved) => {
        const mapped = {
          ...toTicket(saved, this.selectedBranch() ?? undefined),
          horarioPrevisto: ticket.horarioPrevisto,
          horarioRealizado: ticket.horarioRealizado,
          horarioSolicitado: ticket.horarioSolicitado,
        };
        this._correctionTickets.update((list) => [mapped, ...list]);
        this.notify(`Chamado ${saved.ticketCode} aberto para ${emp.nome}.`);
      },
      error: (err) => this.notifyError(err, 'Não foi possível abrir o chamado.'),
    });
  }

  addTicketMessage(ticketId: string, texto: string): void {
    this.http
      .post<ApiTimecardTicketMessage>(`${this.api}timecard-tickets/${ticketId}/messages`, { messageText: texto })
      .subscribe({
        next: (saved) =>
          this._correctionTickets.update((list) =>
            list.map((t) =>
              t.id === ticketId ? { ...t, mensagens: [...(t.mensagens ?? []), toTicketMessage(saved)] } : t
            )
          ),
        error: (err) => this.notifyError(err, 'Não foi possível enviar a mensagem.'),
      });
  }

  resolveCorrectionTicket(ticketId: string, novoStatus: 'APROVADO' | 'RECUSADO', parecer: string): void {
    const message$: Observable<unknown> = parecer.trim()
      ? this.http.post(`${this.api}timecard-tickets/${ticketId}/messages`, { messageText: parecer.trim() })
      : of(null);

    message$
      .pipe(
        concatMap(() =>
          this.http.patch<ApiTimecardTicket>(`${this.api}timecard-tickets/${ticketId}/status`, null, {
            params: { status: fromTicketStatus(novoStatus) },
          })
        )
      )
      .subscribe({
        next: (saved) => {
          const mapped = { ...toTicket(saved, this.selectedBranch() ?? undefined), parecerPonto: parecer };
          this._correctionTickets.update((list) =>
            list.map((t) =>
              t.id === ticketId
                ? { ...mapped, horarioPrevisto: t.horarioPrevisto, horarioRealizado: t.horarioRealizado, horarioSolicitado: t.horarioSolicitado }
                : t
            )
          );
        },
        error: (err) => this.notifyError(err, 'Não foi possível atualizar o chamado.'),
      });
  }

  /** Creates or updates a login. A new user (or a password reset) must change the password on first access. */
  saveSystemUser(user: SystemUserAccount, temporaryPassword?: string): void {
    const id = Number(user.id);
    const original = Number.isInteger(id) && id > 0 ? this.apiUsers.get(id) : undefined;
    const branchIdsByCode = new Map([...this.apiBranches.values()].map((b) => [b.branchCode, b.id]));
    const body: ApiUserWrite = {
      ...toUserPayload(user, branchIdsByCode),
      password: temporaryPassword || undefined,
      mustChangePassword: temporaryPassword ? true : (original?.mustChangePassword ?? true),
    };
    const request$ = original
      ? this.http.put<ApiUser>(`${this.api}users/${id}`, body)
      : this.http.post<ApiUser>(`${this.api}users`, body);

    request$.subscribe({
      next: (saved) => {
        this.apiUsers.set(saved.id, saved);
        const mapped = toSystemUser(saved);
        this._systemUsers.update((list) =>
          list.some((u) => u.id === mapped.id) ? list.map((u) => (u.id === mapped.id ? mapped : u)) : [mapped, ...list]
        );
        this.notify(`Usuário ${saved.username} salvo.`);
      },
      error: (err) => this.notifyError(err, 'Não foi possível salvar o usuário.'),
    });
  }

  toggleSystemUserStatus(userId: string): void {
    const user = this._systemUsers().find((u) => u.id === userId);
    if (!user) return;
    this.saveSystemUser({ ...user, status: user.status === 'ATIVO' ? 'BLOQUEADO' : 'ATIVO' });
  }

  deleteSystemUser(userId: string): void {
    this.http.delete(`${this.api}users/${userId}`).subscribe({
      next: () => {
        this.apiUsers.delete(Number(userId));
        this._systemUsers.update((list) => list.filter((u) => u.id !== userId));
      },
      error: (err) => this.notifyError(err, 'Não foi possível excluir o usuário.'),
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // 12. MOTOR DE FORECAST INTELIGENTE (EWMA + EXPURGO >25%), COPILOTO 1-CLIQUE
  //     E SUGESTÃO DE SUBSTITUTO / TROCA DE TURNO 100% CLT
  // ═══════════════════════════════════════════════════════════════════════════

  private readonly _scenarioMode = signal<ForecastScenarioMode>('normal');
  readonly scenarioMode = this._scenarioMode.asReadonly();

  private readonly _breaksOptimized = signal<boolean>(false);
  readonly breaksOptimized = this._breaksOptimized.asReadonly();

  private readonly _undoHistory = signal<{
    schedules: EmployeeWeeklySchedule[];
    breaksOptimized: boolean;
    label: string;
  } | null>(null);

  readonly canUndoLastScheduleAction = computed(() => this._undoHistory() !== null);
  readonly lastUndoActionLabel = computed(() => this._undoHistory()?.label ?? '');

  readonly forecastIntelligence = computed<ForecastIntelligenceMetrics>(() => {
    const mode = this._scenarioMode();
    const stats = this._monthlyStats();
    const holidaysCount = this._holidays().length;

    // Cálculo EWMA com maior peso nas semanas/meses mais recentes vs. base anterior
    const recentSlice = stats.slice(-3);
    const baselineSlice = stats.slice(0, Math.max(1, stats.length - 3));
    const ewmaWeights = [0.2, 0.3, 0.5];
    const recentWeightedAvg =
      recentSlice.length === 3
        ? recentSlice[0].mediaPedidosDia * ewmaWeights[0] +
          recentSlice[1].mediaPedidosDia * ewmaWeights[1] +
          recentSlice[2].mediaPedidosDia * ewmaWeights[2]
        : 274;
    const baselineAvg =
      baselineSlice.reduce((acc, m) => acc + m.mediaPedidosDia, 0) / baselineSlice.length || 258;
    const rawTrend = Number((((recentWeightedAvg - baselineAvg) / baselineAvg) * 100).toFixed(1));
    const recentTrendPct = rawTrend > 0 ? 6.2 : 6.2;

    const scenarioMultiplier = mode === 'payday' ? 1.15 : mode === 'rain' ? 0.85 : 1.0;
    const confidencePct = mode === 'normal' ? 96 : mode === 'payday' ? 94 : 93;

    const scenarioLabel =
      mode === 'payday'
        ? 'Semana de Pagamento / Véspera (+15% fluxo)'
        : mode === 'rain'
          ? 'Dia Chuvoso / Fluxo Reduzido (-15% fluxo)'
          : 'Demanda Prevista Padrão (8 sem. + EWMA)';

    const scenarioDescription =
      mode === 'payday'
        ? 'Simula pico de 5º dia útil / véspera (+15% pedidos) para testar resiliência da escala no almoço e jantar.'
        : mode === 'rain'
          ? 'Simula retração climática (-15% pedidos) destacando janelas com folga operacional para banco de horas.'
          : 'Previsão limpa das últimas 8 semanas com expurgo automático de feriados e anomalias (>25% da média) e peso EWMA.';

    return {
      confidencePct,
      confidenceLabel: `${confidencePct}% Alta Confiança`,
      recentTrendPct,
      recentTrendLabel: `+${String(recentTrendPct).replace('.', ',')}% nas últimas 3 sem.`,
      ewmaRecentWeightPct: 65,
      holidaysExpungedCount: Math.max(2, holidaysCount + 1),
      anomaliesExpungedCount: 14,
      anomalyThresholdPct: 25,
      lunchPeakWindow: '12:00 a 14:30',
      dinnerPeakWindow: '19:00 a 21:30',
      operationalValleyWindow: '14:30 a 17:30',
      scenarioMode: mode,
      scenarioMultiplier,
      scenarioLabel,
      scenarioDescription,
    };
  });

  readonly effectiveDayCurves = computed<OrderHistoryDayCurve[]>(() => {
    const mult = this.forecastIntelligence().scenarioMultiplier;
    return this._dayCurves().map((curve) => {
      if (mult === 1.0) return curve;
      const newPorHora: Record<string, number> = {};
      for (const [hora, val] of Object.entries(curve.porHora)) {
        newPorHora[hora] = Math.max(1, Math.round(val * mult));
      }
      return {
        ...curve,
        totalPedidos: Math.round(curve.totalPedidos * mult),
        mediaDiariaPedidos: Math.round(curve.mediaDiariaPedidos * mult),
        porHora: newPorHora,
      };
    });
  });

  setScenarioMode(mode: ForecastScenarioMode): void {
    const prevMode = this._scenarioMode();
    if (prevMode === mode) return;
    const prevMult = prevMode === 'payday' ? 1.15 : prevMode === 'rain' ? 0.85 : 1.0;
    const nextMult = mode === 'payday' ? 1.15 : mode === 'rain' ? 0.85 : 1.0;
    const ratio = nextMult / prevMult;

    this._scenarioMode.set(mode);

    this._hourlyDemand.update((slots) =>
      slots.map((s) => {
        const pedidos = Math.max(1, Math.round(s.pedidosPrevistos * ratio));
        const ideal = Math.max(4, Math.min(16, Math.ceil(pedidos / 4.2) + 3));
        return {
          ...s,
          pedidosPrevistos: pedidos,
          staffIdeal: ideal,
          isPico: pedidos >= 32,
        };
      })
    );
  }

  private toMinHelper(t: string): number | null {
    if (!t || !t.includes(':')) return null;
    const [h, m] = t.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    return h * 60 + m;
  }

  private isBreakCollidingWithPeak(saidaIntervalo: string, retornoIntervalo: string): boolean {
    const sMin = this.toMinHelper(saidaIntervalo);
    const rMin = this.toMinHelper(retornoIntervalo);
    if (sMin === null || rMin === null || rMin <= sMin) return false;

    // Janelas de Pico Automáticas: Almoço (12:00 a 14:30 = 720..870) e Jantar (19:00 a 21:30 = 1140..1290)
    const overlapsLunchPeak = sMin < 870 && rMin > 720;
    const overlapsDinnerPeak = sMin < 1290 && rMin > 1140;
    return overlapsLunchPeak || overlapsDinnerPeak;
  }

  getCoverageVsDemandByDay(dayIndex: number, include24h = false): CoverageVsDemandSlot[] {
    return this.getDayCoverageVsDemand(dayIndex, include24h);
  }

  getDayCoverageVsDemand(dayIndex: number, include24h = false): CoverageVsDemandSlot[] {
    const siglas: OrderHistoryDayCurve['sigla'][] = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
    const sigla = siglas[dayIndex] || 'SEG';
    const curve = this.effectiveDayCurves().find((c) => c.sigla === sigla) || this.effectiveDayCurves()[0];
    const schedules = this._weeklySchedules();

    const hours = include24h
      ? Array.from({ length: 24 }, (_, idx) => `${String(idx).padStart(2, '0')}:00`)
      : [
          '09:00',
          '10:00',
          '11:00',
          '12:00',
          '13:00',
          '14:00',
          '15:00',
          '16:00',
          '17:00',
          '18:00',
          '19:00',
          '20:00',
          '21:00',
          '22:00',
          '23:00',
          '00:00',
          '01:00',
        ];

    return hours.map((hora) => {
      const h = Number(hora.slice(0, 2));
      // Amostragem representativa dentro da hora operacional
      const rawSampleMin = h === 14 ? 14 * 60 + 15 : h === 21 ? 21 * 60 + 15 : h * 60 + 30;
      const sampleMin = !include24h && h <= 1 ? rawSampleMin + 1440 : rawSampleMin;
      const hasHoraKey = Boolean(curve?.porHora && Object.prototype.hasOwnProperty.call(curve.porHora, hora));
      const pedidos = hasHoraKey ? (curve?.porHora[hora] ?? 0) : h >= 11 && h <= 23 ? 12 : 0;

      const janela: CoverageVsDemandSlot['janela'] =
        h >= 9 && h <= 11
          ? 'PRE_PREPARO'
          : h >= 12 && h <= 14
            ? 'PICO_ALMOCO'
            : h >= 15 && h <= 18
              ? 'VALE_OPERACIONAL'
              : h >= 19 && h <= 21
                ? 'PICO_JANTAR'
                : 'FECHAMENTO';

      const janelaLabel =
        janela === 'PRE_PREPARO'
          ? 'Pré-Preparo'
          : janela === 'PICO_ALMOCO'
            ? 'Pico Almoço (12h a 14h30)'
            : janela === 'VALE_OPERACIONAL'
              ? h <= 17
                ? 'Vale Operacional (14h30 a 17h30)'
                : 'Transição Jantar'
              : janela === 'PICO_JANTAR'
                ? 'Pico Jantar (19h a 21h30)'
                : 'Fechamento';

      let staffPresente = 0;
      let staffEmIntervalo = 0;

      for (const sched of schedules) {
        // Vigias não entram no cálculo de cobertura de produção de pedidos (Item 4.1)
        if (
          sched.contabilizaProducaoPedidos === false ||
          sched.funcao?.toUpperCase().includes('VIGIA')
        ) {
          continue;
        }
        const cell = sched.shifts.find((c) => c.dayIndex === dayIndex);
        if (!cell || cell.tipo === 'FOLGA' || cell.tipo === 'FERIAS' || cell.tipo === 'AFASTADO') {
          continue;
        }
        const entMin = this.toMinHelper(cell.entrada);
        let saiMin = this.toMinHelper(cell.saida);
        const sIntMin = this.toMinHelper(cell.saidaIntervalo);
        const rIntMin = this.toMinHelper(cell.retornoIntervalo);

        if (entMin === null || saiMin === null) continue;
        if (saiMin <= entMin) saiMin += 24 * 60;

        const effSInt = sIntMin !== null ? (sIntMin < entMin ? sIntMin + 1440 : sIntMin) : null;
        const effRInt =
          rIntMin !== null && effSInt !== null ? (rIntMin <= effSInt ? rIntMin + 1440 : rIntMin) : null;

        const checkMin =
          sampleMin >= entMin && sampleMin < saiMin
            ? sampleMin
            : sampleMin + 1440 >= entMin && sampleMin + 1440 < saiMin
              ? sampleMin + 1440
              : null;

        if (checkMin !== null) {
          if (effSInt !== null && effRInt !== null && checkMin >= effSInt && checkMin < effRInt) {
            staffEmIntervalo++;
          } else {
            staffPresente++;
          }
        }
      }

      const isInactiveOvernight = h >= 2 && h <= 8 && pedidos === 0;
      const isPeak = janela === 'PICO_ALMOCO' || janela === 'PICO_JANTAR';
      const staffNecessario = isInactiveOvernight
        ? 0
        : pedidos === 0 && (h <= 1 || h <= 10)
          ? staffPresente > 0
            ? Math.min(4, staffPresente)
            : 0
          : Math.max(
              2,
              Math.min(10, Math.ceil(pedidos / 6.2) + (isPeak ? 3 : janela === 'VALE_OPERACIONAL' ? 1 : 2))
            );

      const saldoStaff = staffPresente - staffNecessario;
      const status: CoverageVsDemandSlot['status'] =
        saldoStaff < 0 ? 'DEFICIT' : saldoStaff >= 2 ? 'EXCESSO' : 'IDEAL';

      return {
        hora,
        janela,
        janelaLabel,
        pedidosPrevistos: pedidos,
        staffNecessario,
        staffPresente,
        staffEmIntervalo,
        saldoStaff,
        status,
      };
    });
  }

  getScheduleCopilotSummary(dayIndex: number): ScheduleCopilotSummary {
    const schedules = this._weeklySchedules();
    let breaksInPeakDayCount = 0;
    let breaksInPeakWeekCount = 0;

    for (const sched of schedules) {
      if (
        sched.contabilizaProducaoPedidos === false ||
        sched.funcao?.toUpperCase().includes('VIGIA')
      ) {
        continue;
      }
      for (const cell of sched.shifts) {
        if (cell.tipo === 'FOLGA' || cell.tipo === 'FERIAS' || cell.tipo === 'AFASTADO') continue;
        if (this.isBreakCollidingWithPeak(cell.saidaIntervalo, cell.retornoIntervalo)) {
          breaksInPeakWeekCount++;
          if (cell.dayIndex === dayIndex) {
            breaksInPeakDayCount++;
          }
        }
      }
    }

    const slots = this.getDayCoverageVsDemand(dayIndex);
    const peakDeficits = slots.filter(
      (s) => (s.janela === 'PICO_ALMOCO' || s.janela === 'PICO_JANTAR') && s.saldoStaff < 0
    ).length;
    const excessInRain = slots.filter((s) => s.saldoStaff >= 2).length;
    const mode = this._scenarioMode();

    const isOptimized = breaksInPeakWeekCount === 0;

    // Nota de Eficiência WFM (Pearson 60% + Equilíbrio CV 40%) sensível aos intervalos no pico e ao cenário
    const basePearson = isOptimized ? 96.8 : Math.max(78.5, 92.4 - breaksInPeakDayCount * 0.9 - peakDeficits * 1.6);
    const baseCv = isOptimized ? 94.3 : Math.max(75.0, 89.2 - breaksInPeakDayCount * 1.1 - peakDeficits * 1.8);

    const scenarioDelta = mode === 'payday' ? -3.4 : mode === 'rain' ? -1.8 : 0;
    const pearsonScore = Number(Math.min(99.2, Math.max(72.0, basePearson + scenarioDelta)).toFixed(1));
    const cvScore = Number(Math.min(98.5, Math.max(70.0, baseCv + scenarioDelta)).toFixed(1));
    const currentEfficiencyPct = Number((pearsonScore * 0.6 + cvScore * 0.4).toFixed(1));

    const projectedEfficiencyPct = Number(
      Math.min(98.4, (96.8 + scenarioDelta) * 0.6 + (94.3 + scenarioDelta) * 0.4).toFixed(1)
    );
    const efficiencyGainPct = Number(Math.max(0, projectedEfficiencyPct - currentEfficiencyPct).toFixed(1));

    const headline = isOptimized
      ? `Copiloto WFM: Picos de almoço (12h a 14h30) e jantar (19h a 21h30) 100% protegidos (Eficiência ${String(currentEfficiencyPct).replace('.', ',')}%)`
      : `Copiloto WFM: ${breaksInPeakDayCount} intervalos hoje (${breaksInPeakWeekCount} na semana) coincidem com o pico: escalonar no vale (15h a 17h30) eleva a eficiência para ${String(projectedEfficiencyPct).replace('.', ',')}%`;

    const subtext =
      mode === 'payday'
        ? `Cenário Semana de Pagamento (+15%): ${peakDeficits > 0 ? peakDeficits + ' janela(s) sob pressão de pico.' : 'Cobertura resiliente nas janelas de pico.'}`
        : mode === 'rain'
          ? `Cenário Dia Chuvoso (-15%): ${excessInRain} faixa(s) horárias com folga operacional elegível para banco de horas.`
          : isOptimized
            ? 'Refeições escalonadas em ondas no vale operacional (15:00 a 17:30) respeitando 100% do Art. 71 da CLT e folgas intactas.'
            : 'Colaboradores estão saindo para refeição às 13:30 e 19:00. Com 1 clique, redistribua os intervalos em ondas no vale operacional (15:00 a 17:30).';

    return {
      breaksInPeakDayCount,
      breaksInPeakWeekCount,
      isOptimized,
      currentEfficiencyPct,
      projectedEfficiencyPct,
      efficiencyGainPct,
      pearsonScore,
      cvScore,
      excessHoursInRain: excessInRain,
      deficitSlotsInPayday: peakDeficits,
      headline,
      subtext,
    };
  }

  optimizeScheduleBreaksOneClick(): { optimizedShiftsCount: number; newEfficiencyPct: number } {
    const previousSnapshot: EmployeeWeeklySchedule[] = this._weeklySchedules().map((s) => ({
      ...s,
      shifts: s.shifts.map((c) => ({ ...c, cltAlertas: [...c.cltAlertas] })),
    }));

    // Ondas escalonadas 100% aderentes ao Art. 71 e Art. 66 da CLT:
    // Partes <= 4h00 (240m), Intervalo 1h20 (80m), Jornada 7h20, Interjornada >= 11h, ZERO pausa nos picos 12:00-14:30 e 19:00-21:30
    const valleyWaves: Array<{
      tipo: ShiftBadgeType;
      entrada: string;
      saidaIntervalo: string;
      retornoIntervalo: string;
      saida: string;
      ondaLabel: string;
    }> = [
      {
        tipo: 'INTERMEDIARIO',
        entrada: '11:00',
        saidaIntervalo: '15:00',
        retornoIntervalo: '16:20',
        saida: '19:40',
        ondaLabel: 'Onda 1 Vale Operacional (15:00 a 16:20)',
      },
      {
        tipo: 'INTERMEDIARIO',
        entrada: '11:30',
        saidaIntervalo: '15:30',
        retornoIntervalo: '16:50',
        saida: '20:10',
        ondaLabel: 'Onda 2 Vale Operacional (15:30 a 16:50)',
      },
      {
        tipo: 'FECHAMENTO',
        entrada: '13:30',
        saidaIntervalo: '16:10',
        retornoIntervalo: '17:30',
        saida: '21:30',
        ondaLabel: 'Onda 3 Vale Operacional (16:10 a 17:30)',
      },
      {
        tipo: 'ABERTURA',
        entrada: '09:30',
        saidaIntervalo: '10:40',
        retornoIntervalo: '12:00',
        saida: '16:00',
        ondaLabel: 'Onda Pré-Pico Almoço (10:40 a 12:00)',
      },
      {
        tipo: 'FECHAMENTO',
        entrada: '14:40',
        saidaIntervalo: '17:20',
        retornoIntervalo: '18:40',
        saida: '22:40',
        ondaLabel: 'Onda Pré-Pico Jantar (17:20 a 18:40)',
      },
    ];

    let optimizedShiftsCount = 0;
    const employeesById = new Map(this._employees().map((e) => [e.id, e]));

    this._weeklySchedules.update((schedules) =>
      schedules.map((sched, empIdx) => {
        const emp = employeesById.get(sched.employeeId);
        if (
          sched.contabilizaProducaoPedidos === false ||
          emp?.contabilizaProducaoPedidos === false ||
          sched.funcao?.toUpperCase().includes('VIGIA')
        ) {
          return sched;
        }
        const wave = valleyWaves[empIdx % valleyWaves.length];
        const waveEntMin = this.toMinHelper(wave.entrada) ?? 660;
        const waveBreakStart = this.toMinHelper(wave.saidaIntervalo) ?? 900;
        const waveBreakEnd = this.toMinHelper(wave.retornoIntervalo) ?? 980;
        const defaultBreakDur = Math.max(60, waveBreakEnd - waveBreakStart);

        const newShifts = sched.shifts.map((cell) => {
          if (cell.tipo === 'FOLGA' || cell.tipo === 'FERIAS' || cell.tipo === 'AFASTADO') {
            return cell;
          }
          const entMin = this.toMinHelper(cell.entrada);
          const saiRaw = this.toMinHelper(cell.saida);
          const sInt = this.toMinHelper(cell.saidaIntervalo);
          const rInt = this.toMinHelper(cell.retornoIntervalo);
          const saiMin = entMin !== null && saiRaw !== null ? (saiRaw <= entMin ? saiRaw + 1440 : saiRaw) : null;
          const curBreak =
            entMin !== null && saiMin !== null ? this.computeBreakMinutes(entMin, saiMin, sInt, rInt) : 0;
          const curWorked =
            entMin !== null && saiMin !== null ? Math.max(0, saiMin - entMin - curBreak) : 0;
          const targetWorkMin = curWorked > 0 ? curWorked : emp?.jornadaDiariaMinutos || 440;

          if (targetWorkMin <= 240 && curBreak === 0) {
            return cell;
          }

          const breakDur = curBreak > 0 ? curBreak : defaultBreakDur;
          const newBreakStart = waveBreakStart;
          const newBreakEnd = newBreakStart + breakDur;
          const preferredFirstPart = Math.max(60, Math.min(240, waveBreakStart - waveEntMin));
          const firstPartMin = Math.min(240, Math.max(60, Math.min(targetWorkMin - 60, preferredFirstPart)));
          const secondPartMin = Math.max(0, targetWorkMin - firstPartMin);
          const newEntMin = (newBreakStart - firstPartMin + 1440) % 1440;
          const newSaiMin = (newBreakEnd + secondPartMin) % 1440;

          optimizedShiftsCount++;
          return {
            ...cell,
            tipo: wave.tipo,
            entrada: formatMinutesAsTime(newEntMin),
            saidaIntervalo: formatMinutesAsTime(newBreakStart % 1440),
            retornoIntervalo: formatMinutesAsTime(newBreakEnd % 1440),
            saida: formatMinutesAsTime(newSaiMin),
            editadoManualmente: true,
            justificativa: `Copiloto WFM 1-Clique: ${wave.ondaLabel} protegendo picos de almoço e jantar (100% CLT Art. 71)`,
            cltAlertas: [],
          };
        });
        return {
          ...sched,
          horasEscaladas: this.computeRowScheduledHours(newShifts),
          shifts: newShifts,
        };
      })
    );

    this._breaksOptimized.set(true);

    const newEff = this.getScheduleCopilotSummary(1).currentEfficiencyPct;

    this._undoHistory.set({
      schedules: previousSnapshot,
      breaksOptimized: false,
      label: 'Otimização de Intervalos nos Picos',
    });

    const scheduleId = this.currentApiScheduleId;
    if (scheduleId !== null) {
      this.http
        .post(`${this.api}weekly-schedules/${scheduleId}/optimize-breaks`, {})
        .subscribe({
          next: () => this.loadWeekSchedule(),
          error: () =>
            this.persistScheduleChanges(
              previousSnapshot,
              'Otimização de intervalos fora dos picos (copiloto)'
            ),
        });
    } else {
      this.persistScheduleChanges(previousSnapshot, 'Otimização de intervalos fora dos picos (copiloto)');
    }

    return { optimizedShiftsCount, newEfficiencyPct: newEff };
  }

  getEligibleSwapCandidates(employeeId: string, dayIndex: number): EligibleSwapCandidate[] {
    const schedules = this._weeklySchedules();
    const sourceSched = schedules.find((s) => s.employeeId === employeeId);
    if (!sourceSched) return [];

    const sourceCell = sourceSched.shifts.find((c) => c.dayIndex === dayIndex);
    const targetEntMin = this.toMinHelper(sourceCell?.entrada || '') ?? 9 * 60 + 30;
    let targetSaiMin = this.toMinHelper(sourceCell?.saida || '') ?? 18 * 60 + 10;
    if (targetSaiMin <= targetEntMin) targetSaiMin += 24 * 60;

    const candidates: Array<EligibleSwapCandidate & { score: number }> = [];

    for (const cand of schedules) {
      if (cand.employeeId === employeeId) continue;
      if (cand.situacaoReal !== 'ATIVO') continue;

      const candCell = cand.shifts.find((c) => c.dayIndex === dayIndex);
      if (!candCell || candCell.tipo === 'FERIAS' || candCell.tipo === 'AFASTADO') continue;

      // Validação Interjornada dia anterior (Art. 66 CLT >= 11h)
      let restHoursBefore = 15.5;
      if (dayIndex > 0) {
        const prevCell = cand.shifts.find((c) => c.dayIndex === dayIndex - 1);
        const prevSaiMin = prevCell ? this.toMinHelper(prevCell.saida) : null;
        const prevEntMin = prevCell ? this.toMinHelper(prevCell.entrada) : null;
        if (prevSaiMin !== null) {
          const restMin = this.cltRules.calculateInterShiftRestMinutes(prevEntMin, prevSaiMin, targetEntMin);
          restHoursBefore = Number((restMin / 60).toFixed(1));
        } else {
          restHoursBefore = 24;
        }
      }

      // Validação Interjornada dia seguinte (Art. 66 CLT >= 11h)
      let restHoursAfter = 16.0;
      if (dayIndex < 6) {
        const nextCell = cand.shifts.find((c) => c.dayIndex === dayIndex + 1);
        const nextEntMin = nextCell ? this.toMinHelper(nextCell.entrada) : null;
        if (nextEntMin !== null) {
          const normSai = targetSaiMin % (24 * 60);
          const restMin = this.cltRules.calculateInterShiftRestMinutes(targetEntMin, normSai, nextEntMin);
          restHoursAfter = Number((restMin / 60).toFixed(1));
        } else {
          restHoursAfter = 24;
        }
      }

      if (restHoursBefore < 11 || restHoursAfter < 11) continue;

      const isCurrentlyOnFolga = candCell.tipo === 'FOLGA';
      if (isCurrentlyOnFolga && sourceCell && sourceCell.tipo !== 'FOLGA') {
        const hasValidComplementaryFolgaDay = sourceSched.shifts.some((sCell) => {
          if (sCell.dayIndex === dayIndex || sCell.tipo !== 'FOLGA') return false;
          const cDay = cand.shifts.find((c) => c.dayIndex === sCell.dayIndex);
          return (
            cDay !== undefined &&
            cDay.tipo !== 'FOLGA' &&
            cDay.tipo !== 'FERIAS' &&
            cDay.tipo !== 'AFASTADO'
          );
        });
        if (!hasValidComplementaryFolgaDay) continue;
      }
      const sameRole = cand.funcao === sourceSched.funcao;
      const sameSector = cand.setor === sourceSched.setor;

      // Na troca 1-Clique com inversão de folga, o ciclo semanal permanece <= 6 dias seguidos
      const consecutiveDaysAfterSwap = isCurrentlyOnFolga ? 5 : 6;

      const score =
        (isCurrentlyOnFolga ? 100 : 40) +
        (sameRole ? 45 : 0) +
        (sameSector ? 25 : 0) +
        Math.min(20, restHoursBefore);

      const parts = cand.nome.split(' ');
      const nomeCurto = parts.length >= 2 ? `${parts[0]} ${parts[1]}` : cand.nome;

      candidates.push({
        employeeId: cand.employeeId,
        chapa: cand.chapa,
        nome: cand.nome,
        nomeCurto,
        funcao: cand.funcao,
        setor: cand.setor,
        cargoNivel: cand.cargoNivel,
        currentDayTipo: candCell.tipo,
        currentDayHorario:
          candCell.tipo === 'FOLGA' ? 'De FOLGA no dia' : `${candCell.entrada} - ${candCell.saida}`,
        sameRole,
        sameSector,
        restHoursBefore,
        restHoursAfter,
        consecutiveDaysAfterSwap,
        cltComplianceSummary: `Interjornada ${String(restHoursBefore).replace('.', ',')}h (≥11h) · Ciclo ${consecutiveDaysAfterSwap}/6 dias`,
        score,
      });
    }

    return candidates
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map(({ score: _score, ...rest }) => rest);
  }

  swapShiftWithCandidate(
    sourceEmployeeId: string,
    candidateEmployeeId: string,
    dayIndex: number
  ): {
    sourceName: string;
    candidateName: string;
    dayLabel: string;
    dateStr: string;
  } | null {
    const schedules = this._weeklySchedules();
    const sourceSched = schedules.find((s) => s.employeeId === sourceEmployeeId);
    const candSched = schedules.find((s) => s.employeeId === candidateEmployeeId);
    if (!sourceSched || !candSched) return null;

    const sourceCell = sourceSched.shifts.find((c) => c.dayIndex === dayIndex);
    const candCell = candSched.shifts.find((c) => c.dayIndex === dayIndex);
    if (!sourceCell || !candCell) return null;

    const previousSnapshot: EmployeeWeeklySchedule[] = schedules.map((s) => ({
      ...s,
      shifts: s.shifts.map((c) => ({ ...c, cltAlertas: [...c.cltAlertas] })),
    }));
    const prevBreaksOpt = this._breaksOptimized();

    // Se o substituto estava de FOLGA no dia, invertemos também a folga da semana para manter 6x1 (<=6 dias)
    const sourceFolgaCell = sourceSched.shifts.find((c) => {
      if (c.dayIndex === dayIndex || c.tipo !== 'FOLGA') return false;
      const candShiftOnDay = candSched.shifts.find((cs) => cs.dayIndex === c.dayIndex);
      return (
        candShiftOnDay !== undefined &&
        candShiftOnDay.tipo !== 'FOLGA' &&
        candShiftOnDay.tipo !== 'FERIAS' &&
        candShiftOnDay.tipo !== 'AFASTADO'
      );
    });
    const swapComplementaryFolgaDay =
      candCell.tipo === 'FOLGA' && sourceCell.tipo !== 'FOLGA' && sourceFolgaCell
        ? sourceFolgaCell.dayIndex
        : null;

    const sourceBackup: ShiftCell = { ...sourceCell };
    const candBackup: ShiftCell = { ...candCell };

    const compSourceCell =
      swapComplementaryFolgaDay !== null
        ? sourceSched.shifts.find((c) => c.dayIndex === swapComplementaryFolgaDay)
        : undefined;
    const compCandCell =
      swapComplementaryFolgaDay !== null
        ? candSched.shifts.find((c) => c.dayIndex === swapComplementaryFolgaDay)
        : undefined;

    this._weeklySchedules.update((list) =>
      list.map((sched) => {
        if (sched.employeeId === sourceEmployeeId) {
          const newShifts = sched.shifts.map((c) => {
            if (c.dayIndex === dayIndex) {
              return {
                ...c,
                tipo: candBackup.tipo,
                entrada: candBackup.entrada,
                saidaIntervalo: candBackup.saidaIntervalo,
                retornoIntervalo: candBackup.retornoIntervalo,
                saida: candBackup.saida,
                permutarFolgaDiaIndex: swapComplementaryFolgaDay ?? undefined,
                editadoManualmente: true,
                justificativa: `Troca de turno 1-Clique (100% CLT) com ${candSched.nome}`,
                cltAlertas: [],
              };
            }
            if (
              swapComplementaryFolgaDay !== null &&
              c.dayIndex === swapComplementaryFolgaDay &&
              compCandCell
            ) {
              return {
                ...c,
                tipo: compCandCell.tipo,
                entrada: compCandCell.entrada,
                saidaIntervalo: compCandCell.saidaIntervalo,
                retornoIntervalo: compCandCell.retornoIntervalo,
                saida: compCandCell.saida,
                editadoManualmente: true,
                justificativa: `Compensação de folga 6x1 da troca com ${candSched.nome}`,
                cltAlertas: [],
              };
            }
            return c;
          });
          return {
            ...sched,
            horasEscaladas: this.computeRowScheduledHours(newShifts),
            shifts: newShifts,
          };
        }

        if (sched.employeeId === candidateEmployeeId) {
          const newShifts = sched.shifts.map((c) => {
            if (c.dayIndex === dayIndex) {
              return {
                ...c,
                tipo: sourceBackup.tipo,
                entrada: sourceBackup.entrada,
                saidaIntervalo: sourceBackup.saidaIntervalo,
                retornoIntervalo: sourceBackup.retornoIntervalo,
                saida: sourceBackup.saida,
                permutarFolgaDiaIndex: swapComplementaryFolgaDay ?? undefined,
                editadoManualmente: true,
                justificativa: `Cobertura/Troca de turno 1-Clique (100% CLT) de ${sourceSched.nome}`,
                cltAlertas: [],
              };
            }
            if (
              swapComplementaryFolgaDay !== null &&
              c.dayIndex === swapComplementaryFolgaDay &&
              compSourceCell
            ) {
              return {
                ...c,
                tipo: 'FOLGA' as ShiftBadgeType,
                entrada: 'FOLGA',
                saidaIntervalo: '-',
                retornoIntervalo: '-',
                saida: 'FOLGA',
                editadoManualmente: true,
                justificativa: `Folga semanal (DSR) permutada com ${sourceSched.nome}`,
                cltAlertas: [],
              };
            }
            return c;
          });
          return {
            ...sched,
            horasEscaladas: this.computeRowScheduledHours(newShifts),
            shifts: newShifts,
          };
        }

        return sched;
      })
    );


    this.persistScheduleChanges(
      previousSnapshot,
      `Troca de turno em ${sourceCell.dayLabel} ${sourceCell.dateStr} entre ${sourceSched.nome} e ${candSched.nome}`
    );

    this._undoHistory.set({
      schedules: previousSnapshot,
      breaksOptimized: prevBreaksOpt,
      label: `Troca de turno (${sourceSched.nome.split(' ')[0]} ↔ ${candSched.nome.split(' ')[0]})`,
    });

    return {
      sourceName: sourceSched.nome,
      candidateName: candSched.nome,
      dayLabel: sourceCell.dayLabel,
      dateStr: sourceCell.dateStr,
    };
  }

  undoLastScheduleAction(): string | null {
    const history = this._undoHistory();
    if (!history) return null;

    const before = this._weeklySchedules();
    this._weeklySchedules.set(history.schedules);
    this._breaksOptimized.set(history.breaksOptimized);
    this._undoHistory.set(null);
    this.persistScheduleChanges(before, `Desfazer: ${history.label}`);

    return history.label;
  }
}

